/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class BinaryUtils {
    static stringToUint8Array(string) {
        let size = 0;
        for (let i = 0, len = string.length; i < len; i++) {
            const charcode = string.charCodeAt(i);
            if (charcode < 0x80) {
                size++;
            }
            else if (charcode < 0x800) {
                size += 2;
            }
            else if (charcode < 0xd800 || charcode >= 0xe000) {
                size += 3;
            }
            else {
                size += 4;
            }
        }
        const out = new Uint8Array(size);
        let index = 0;
        for (let i = 0, len = string.length; i < len; i++) {
            let charcode = string.charCodeAt(i);
            if (charcode < 0x80) {
                out[index++] = charcode;
            }
            else if (charcode < 0x800) {
                out[index++] = 0xc0 | (charcode >> 6);
                out[index++] = 0x80 | (charcode & 0x3f);
            }
            else if (charcode < 0xd800 || charcode >= 0xe000) {
                out[index++] = 0xe0 | (charcode >> 12);
                out[index++] = 0x80 | ((charcode >> 6) & 0x3f);
                out[index++] = 0x80 | (charcode & 0x3f);
            }
            else {
                i++;
                charcode = 0x10000 + (((charcode & 0x3ff) << 10) | (string.charCodeAt(i) & 0x3ff));
                out[index++] = 0xf0 | (charcode >> 18);
                out[index++] = 0x80 | ((charcode >> 12) & 0x3f);
                out[index++] = 0x80 | ((charcode >> 6) & 0x3f);
                out[index++] = 0x80 | (charcode & 0x3f);
            }
        }
        return out;
    }
    static uint8ToStringArray(array) {
        let str = "";
        const end = array.length;
        let i = 0;
        while (i < end) {
            const value = array[i];
            i++;
            if (value < 0x80) {
                str += String.fromCharCode(value);
            }
            else if (value < 0xE0 && value > 0xBF) {
                str += String.fromCharCode((value & 0x1F) << 6 | array[i++] & 0x3F);
            }
            else if (value < 0xF0 && value > 0xDF) {
                str += String.fromCharCode((value & 0x0F) << 12 | (array[i++] & 0x3F) << 6 | array[i++] & 0x3F);
            }
            else {
                const charCode = ((value & 0x07) << 18 | (array[i++] & 0x3F) << 12 | (array[i++] & 0x3F) << 6 | array[i++] & 0x3F) - 0x010000;
                str += String.fromCharCode(charCode >> 10 | 0xD800, charCode & 0x03FF | 0xDC00);
            }
        }
        return str;
    }
}

class Base85 {
    constructor(map) {
        if (map.length !== 85) {
            throw new Error();
        }
        let chars = [];
        for (let i = 0; i < map.length; i++) {
            chars.push(map.charAt(i));
        }
        let rev = {};
        chars.forEach((c, i) => { rev[c] = i; });
        this.chars = chars;
        this.rev = rev;
    }
    encodeString(text) {
        return this.encode(BinaryUtils.stringToUint8Array(text));
    }
    encode(array) {
        let chars = this.chars;
        let out = "";
        let length = array.length;
        let groups = Math.floor(array.length / 4);
        for (let g = 0; g < groups; g++) {
            let number = 0;
            for (let i = 0; i < 4; i++) {
                number = (number * 256) + array[g * 4 + i];
            }
            let divider = 85 * 85 * 85 * 85;
            for (let i = 0; i < 5; i++) {
                out += chars[((number / divider) >>> 0) % 85];
                divider /= 85;
            }
        }
        let lastIndex = groups * 4;
        let lastSize = length - lastIndex;
        if (lastSize !== 0) {
            let number = 0;
            for (let i = 0; i < lastSize; i++) {
                number = (number * 256) + array[lastIndex + i];
            }
            let divider = 85;
            if (lastSize === 3) {
                divider = 85 * 85 * 85;
            }
            else if (lastSize === 2) {
                divider = 85 * 85;
            }
            for (let i = 0; i < lastSize + 1; i++) {
                out += chars[((number / divider) >>> 0) % 85];
                divider /= 85;
            }
        }
        return out;
    }
    decodeString(text) {
        return BinaryUtils.uint8ToStringArray(this.decode(text));
    }
    decode(array) {
        let rev = this.rev;
        let length = array.length;
        let groups = Math.floor(array.length / 5);
        let lastIndex = groups * 5;
        let lastSize = length - lastIndex;
        let out = new Uint8Array(groups * 4 + (lastSize > 1 ? lastSize - 1 : 0));
        let index = 0;
        for (let g = 0; g < groups; g++) {
            let number = 0;
            for (let i = 0; i < 5; i++) {
                number = number * 85 + rev[array[g * 5 + i]];
            }
            let divider = 256 * 256 * 256;
            for (let i = 0; i < 4; i++) {
                out[index++] = ((number / divider) >>> 0) % 256;
                divider /= 256;
            }
        }
        if (lastSize !== 0) {
            let number = 0;
            for (let i = 0; i < lastSize; i++) {
                number = number * 85 + rev[array[lastIndex + i]];
            }
            let divider = 1;
            if (lastSize === 4) {
                divider = 256 * 256;
            }
            else if (lastSize === 3) {
                divider = 256;
            }
            for (let i = 0; i < lastSize - 1; i++) {
                out[index++] = ((number / divider) >>> 0) % 256;
                divider /= 256;
            }
        }
        return out;
    }
    intToString(number) {
        number = number >>> 0;
        let out = "";
        let divider = 85 * 85 * 85 * 85;
        for (let i = 0; i < 5; i++) {
            out += this.chars[((number / divider) >>> 0) % 85];
            divider /= 85;
        }
        return out;
    }
    stringToInt(str) {
        let number = 0;
        for (let i = 0; i < 5; i++) {
            number = number * 85 + this.rev[str[i]];
        }
        return number >>> 0;
    }
}
Base85.default = new Base85("0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ.-:+=^!/*?&_;()[]{}@%$#");

class FormatUtils {
    static fileSize(size, names) {
        var _a;
        let negative = size < 0;
        size = Math.abs(size);
        let i = Math.floor(Math.log(size) / Math.log(1024) + 0.336);
        let value = +((size / Math.pow(1024, i)).toFixed(2));
        return `${negative ? "-" : ""}${(value).toString().substring(0, 4)}${(_a = names[i]) !== null && _a !== void 0 ? _a : "??"}`;
    }
    static fileSizeShort(size) {
        return FormatUtils.fileSize(size, FormatUtils.fileSizesShort);
    }
    static fileSizeShortNoSpace(size) {
        return FormatUtils.fileSize(size, FormatUtils.fileSizesShortNoSpace);
    }
    static fileSizeLong(size) {
        return FormatUtils.fileSize(size, FormatUtils.fileSizesLong);
    }
    static timeAgo(diffOrDate, names = FormatUtils.timeLong) {
        let diff;
        if (diffOrDate instanceof Date) {
            diff = Date.now() - diffOrDate.getTime();
        }
        else {
            diff = diffOrDate;
        }
        let seconds = Math.floor(diff / 1000);
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);
        let months = Math.floor(days / 30);
        let years = Math.floor(months / 12);
        if (years > 0) {
            return `${years} ${years === 1 ? names.years[0] : names.years[1]}`;
        }
        else if (months > 0) {
            return `${months} ${months === 1 ? names.months[0] : names.months[1]}`;
        }
        else if (days > 0) {
            return `${days} ${days === 1 ? names.days[0] : names.days[1]}`;
        }
        else if (hours > 0) {
            return `${hours} ${hours === 1 ? names.hours[0] : names.hours[1]}`;
        }
        else if (minutes > 0) {
            return `${minutes} ${minutes === 1 ? names.minutes[0] : names.minutes[1]}`;
        }
        else if (seconds >= 0) {
            return `${seconds} ${seconds === 1 ? names.seconds[0] : names.seconds[1]}`;
        }
        else {
            return false;
        }
    }
    static timeFromNow(diffOrDate, names = FormatUtils.timeLong) {
        let diff;
        if (diffOrDate instanceof Date) {
            diff = diffOrDate.getTime() - Date.now();
        }
        else {
            diff = diffOrDate;
        }
        let seconds = Math.floor(diff / 1000);
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);
        let months = Math.floor(days / 30);
        let years = Math.floor(months / 12);
        if (years > 0) {
            return `${years} ${years === 1 ? names.years[0] : names.years[1]}`;
        }
        else if (months > 0) {
            return `${months} ${months === 1 ? names.months[0] : names.months[1]}`;
        }
        else if (days > 0) {
            return `${days} ${days === 1 ? names.days[0] : names.days[1]}`;
        }
        else if (hours > 0) {
            return `${hours} ${hours === 1 ? names.hours[0] : names.hours[1]}`;
        }
        else if (minutes > 0) {
            return `${minutes} ${minutes === 1 ? names.minutes[0] : names.minutes[1]}`;
        }
        else if (seconds >= 0) {
            return `${seconds} ${seconds === 1 ? names.seconds[0] : names.seconds[1]}`;
        }
        else {
            return false;
        }
    }
    static time(diffOrDate, names = FormatUtils.timeLong) {
        let diff;
        if (diffOrDate instanceof Date) {
            diff = diffOrDate.getTime() - Date.now();
        }
        else {
            diff = diffOrDate;
        }
        let seconds = Math.floor(diff / 1000);
        if (seconds === 0) {
            return "now";
        }
        else if (diff > 0) {
            return FormatUtils.timeFromNow(diff, names) + " from now";
        }
        else {
            return FormatUtils.timeAgo(-diff, names) + " ago";
        }
    }
    static number(value, names) {
        let negative = value < 0;
        value = Math.abs(value);
        let i = value > 0 ? Math.floor(Math.log(value) / Math.log(1000) + 0.334) : 0;
        let value2 = +(value / Math.pow(1000, i)).toFixed(2);
        return `${negative ? "-" : ""}${(value2).toString().substring(0, 4)}${names[i]}`;
    }
    static numberShort(value) {
        return FormatUtils.number(value, FormatUtils.numberShortNames);
    }
    static numberLong(value) {
        return FormatUtils.number(value, FormatUtils.numberLongNames);
    }
}
FormatUtils.fileSizesShort = [
    " B", " KB", " MB", " GB", " TB", " PB", " EB", " ZB", " YB"
];
FormatUtils.fileSizesShortNoSpace = [
    "B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"
];
FormatUtils.fileSizesLong = [
    " bytes", " kilobytes", " megabytes", " gigabytes", " terabytes", " petabytes", " exabytes", " zettabytes", " yottabytes"
];
FormatUtils.timeShort = {
    "seconds": ["sec", "secs"],
    "minutes": ["min", "mins"],
    "hours": ["hour", "hours"],
    "days": ["day", "days"],
    "months": ["month", "months"],
    "years": ["year", "years"]
};
FormatUtils.timeLong = {
    "seconds": ["second", "seconds"],
    "minutes": ["minute", "minutes"],
    "hours": ["hour", "hours"],
    "days": ["day", "days"],
    "months": ["month", "months"],
    "years": ["year", "years"]
};
FormatUtils.numberShortNames = [
    "", "K", "M", "B", "T", "Q"
];
FormatUtils.numberLongNames = [
    "", " thousand", " million", " billion", " trillion", " quadrillion"
];

class HaxCompressor {
    static compressNumber(num, size) {
        if (num < 0 || num > Number.MAX_SAFE_INTEGER || num !== Math.floor(num)) {
            return ":" + num;
        }
        num = Math.floor(num);
        let text = "";
        let handled = 0;
        let i = 0;
        while (i < 9) {
            let charNumber = Math.floor(i > 0 ? num / Math.pow(HaxCompressor.charsLength, i) : num) % HaxCompressor.charsLength;
            text = HaxCompressor.chars.charAt(charNumber) + text;
            handled += (charNumber) * (Math.pow(HaxCompressor.charsLength, i));
            i++;
            if ((size === undefined || i >= size) && handled >= num) {
                break;
            }
        }
        return text;
    }
    static decompressNumber(text) {
        if (text.startsWith(":")) {
            return +text.substring(1);
        }
        let num = 0;
        for (let i = 0; i < text.length; i++) {
            num *= HaxCompressor.charsLength;
            num += HaxCompressor.charsRev[text[i]];
        }
        return num;
    }
}
HaxCompressor.chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
HaxCompressor.charsRev = {
    "0": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9, "A": 10, "B": 11, "C": 12, "D": 13,
    "E": 14, "F": 15, "G": 16, "H": 17, "I": 18, "J": 19, "K": 20, "L": 21, "M": 22, "N": 23, "O": 24, "P": 25, "Q": 26,
    "R": 27, "S": 28, "T": 29, "U": 30, "V": 31, "W": 32, "X": 33, "Y": 34, "Z": 35, "a": 36, "b": 37, "c": 38, "d": 39,
    "e": 40, "f": 41, "g": 42, "h": 43, "i": 44, "j": 45, "k": 46, "l": 47, "m": 48, "n": 49, "o": 50, "p": 51, "q": 52,
    "r": 53, "s": 54, "t": 55, "u": 56, "v": 57, "w": 58, "x": 59, "y": 60, "z": 61
};
HaxCompressor.charsLength = HaxCompressor.chars.length;

const modulus = 0x80000000;
const multiplier = 1103515245;
const increment = 12345;
const floatUnit = 1.0 / modulus;
/** Toy RNG implementation do not use in production or security applications */
class HaxRNG {
    constructor(seed) {
        seed = (seed >>> 0);
        if (seed < 0) {
            seed = 2345679;
        }
        this.state = seed % modulus;
        this.nextInt(); // init
    }
    nextUInt() {
        return (this.state = (multiplier * this.state + increment) % modulus);
    }
    safeUInt() {
        return (31 * ((this.state = (multiplier * this.state + increment) % modulus) +
            31 * (this.state = (multiplier * this.state + increment) % modulus)) +
            (this.state = (multiplier * this.state + increment) % modulus)) >>> 0;
    }
    nextInt() {
        return (31 * (this.state = (multiplier * this.state + increment) % modulus) +
            (this.state = (multiplier * this.state + increment) % modulus)) | 0;
    }
    boolean(chance = 0.5) {
        return ((this.state = (multiplier * this.state + increment) % modulus) * (floatUnit)) < chance;
    }
    nextFloat() {
        return (this.state = (multiplier * this.state + increment) % modulus) * (floatUnit);
    }
    nextUByte() {
        return ((this.state = (multiplier * this.state + increment) % modulus) >>> 8) & 0xFF;
    }
    nextUShort() {
        return ((this.state = (multiplier * this.state + increment) % modulus) >>> 8) & 0xFFFF;
    }
    /*

    public nextUInt() {
        return (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus);
    }

    public safeUInt() {
        return (
            31 * (
                (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus) +
                31 * (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus)
            ) +
            (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus)
        ) >>> 0;
    }

    public nextInt() {
        return (
            31 * (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus) +
            (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus)
        ) | 0;
    }

    public boolean(chance: number = 0.5) {
        return ((this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus) * (floatUnit)) < chance;
    }

    public nextFloat() {
        return (this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus) * (floatUnit);
    }

    public nextUByte() {
        return ((this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus) >>> 8) & 0xFF;
    }

    public nextUShort() {
        return ((this.state = ((Math.imul(this.state, multiplier) >>> 0) + increment) % modulus) >>> 8) & 0xFFFF;
    }

    */
    nextHChar() {
        return HaxCompressor.chars[this.nextUInt() % HaxCompressor.charsLength];
    }
    rangeInt(min, max) {
        min = (min) | 0;
        max = (max) | 0;
        return ((this.nextFloat() * (max - min)) + min) | 0;
    }
    rangeFloat(min, max) {
        min = +(min);
        max = +(max);
        return +((this.nextFloat() * (max - min)) + min);
    }
    shuffle(array) {
        let len = array.length;
        for (let i = 0; i < len; i++) {
            let j = this.nextUInt() % (i + 1);
            if (i != j) {
                [array[i], array[j]] = [array[j], array[i]];
            }
        }
    }
    pickArraySafe(array) {
        if (array.length <= 0) {
            throw new Error("... it wasn't safe...");
        }
        return array[this.rangeInt(0, array.length)];
    }
    pickArray(array) {
        if (array.length <= 0) {
            return undefined;
        }
        return array[this.rangeInt(0, array.length)];
    }
}

class EventCallbackRef {
    constructor(event, func, target) {
        this.event = event;
        this.func = func;
        this.target = target;
        this.valid = true;
    }
    remove() {
        if (this.valid === true) {
            this.event.removeCallbackRef(this);
            this.valid = false;
            this.event = undefined;
            this.func = undefined;
            this.target = undefined;
        }
    }
}
/** Basic event, provides a way to add callbacks and execute.
 *
 * TFunc is the callback function and KFunc is the execution function
 *
 * Normally TFunc and KFunc will be equal, this is exposed for overriding
 */
class BasicEvent {
    constructor() {
        this.callbacks = [];
        this.executing = false;
    }
    addCallback(func, thisArg) {
        if (!func) {
            throw new Error("Callback can't be null!");
        }
        // Detect the usage of func.bind() - it kills optimization therefore is not allowed.
        if (typeof func.prototype !== "object" && func.name.substr(0, 5) === "bound") {
            throw new Error("Don't use .bind() on functions! (" + func.name + ")");
        }
        const ref = new EventCallbackRef(this, func, thisArg);
        this.callbacks.push(ref);
        return ref;
    }
    clearCallbacks() {
        this.callbacks = [];
    }
    /**
     * Number of callbacks
     */
    get count() {
        return this.callbacks.length;
    }
    removeCallback(func, thisArg) {
        const callbacks = this.executing ? Array.from(this.callbacks) : this.callbacks;
        for (let i = callbacks.length - 1; i >= 0; i--) {
            const callback = callbacks[i];
            if (callback.func === func && callback.target === thisArg) {
                callbacks.splice(i, 1);
            }
        }
        this.callbacks = callbacks;
    }
    removeCallbackRef(ref) {
        const callbacks = this.executing ? Array.from(this.callbacks) : this.callbacks;
        for (let i = callbacks.length - 1; i >= 0; i--) {
            const callback = callbacks[i];
            if (callback === ref) {
                callbacks.splice(i, 1);
            }
        }
        this.callbacks = callbacks;
    }
    hasCallback(func, thisArg) {
        for (let i = this.callbacks.length - 1; i >= 0; i--) {
            const callback = this.callbacks[i];
            if (callback.func === func && callback.target === thisArg) {
                return true;
            }
        }
        return false;
    }
    execute(...args) {
        this.executing = true;
        const callbacks = this.callbacks;
        const length = callbacks.length;
        for (let i = 0; i < length; i++) {
            const callback = callbacks[i];
            if (callback.valid === true) {
                const ret = callback.func.apply(callback.target, args);
                if (ret === BasicEvent.REMOVE_CALLBACK) {
                    callback.remove();
                }
            }
        }
        this.executing = false;
    }
    ;
}
BasicEvent.REMOVE_CALLBACK = Symbol.for("Event::REMOVE");
class PromiseEventCallbackRef {
    constructor(event, func, target) {
        this.event = event;
        this.func = func;
        this.target = target;
        this.valid = true;
    }
    remove() {
        if (this.valid === true) {
            this.event.removeCallbackRef(this);
            this.valid = false;
            this.event = undefined;
            this.func = undefined;
            this.target = undefined;
        }
    }
}
class PromiseEvent {
    constructor() {
        this.callbacks = [];
        this.executing = false;
    }
    addCallback(func, thisArg) {
        if (!func) {
            throw new Error("Callback can't be null!");
        }
        // Detect the usage of func.bind() - it kills optimization therefore is not allowed.
        if (typeof func.prototype !== "object" && func.name.substr(0, 5) === "bound") {
            throw new Error("Don't use .bind() on functions! (" + func.name + ")");
        }
        const ref = new PromiseEventCallbackRef(this, func, thisArg);
        this.callbacks.push(ref);
        return ref;
    }
    clearCallbacks() {
        this.callbacks = [];
    }
    /**
     * Number of callbacks
     */
    get count() {
        return this.callbacks.length;
    }
    removeCallback(func, thisArg) {
        const callbacks = this.executing ? Array.from(this.callbacks) : this.callbacks;
        for (let i = callbacks.length - 1; i >= 0; i--) {
            const callback = callbacks[i];
            if (callback.func === func && callback.target === thisArg) {
                callbacks.splice(i, 1);
            }
        }
        this.callbacks = callbacks;
    }
    removeCallbackRef(ref) {
        const callbacks = this.executing ? Array.from(this.callbacks) : this.callbacks;
        for (let i = callbacks.length - 1; i >= 0; i--) {
            const callback = callbacks[i];
            if (callback === ref) {
                callbacks.splice(i, 1);
            }
        }
        this.callbacks = callbacks;
    }
    hasCallback(func, thisArg) {
        for (let i = this.callbacks.length - 1; i >= 0; i--) {
            const callback = this.callbacks[i];
            if (callback.func === func && callback.target === thisArg) {
                return true;
            }
        }
        return false;
    }
    execute(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            this.executing = true;
            const callbacks = this.callbacks;
            const length = callbacks.length;
            for (let i = 0; i < length; i++) {
                const callback = callbacks[i];
                if (callback.valid === true) {
                    const ret = yield callback.func.apply(callback.target, args);
                    if (ret === BasicEvent.REMOVE_CALLBACK) {
                        callback.remove();
                    }
                }
            }
            this.executing = false;
        });
    }
    ;
}

var ObjectUtils;
(function (ObjectUtils) {
    function deepCopy(input) {
        if (input === null || input === undefined) {
            return input;
        }
        else if (typeof input === "object") {
            if (Array.isArray(input)) {
                input = input.map((x) => ObjectUtils.deepCopy(x));
            }
            else {
                let output = {};
                for (const key in input) {
                    if (Object.prototype.hasOwnProperty.call(input, key)) {
                        const value = input[key];
                        output[key] = ObjectUtils.deepCopy(value);
                    }
                }
                input = output;
            }
        }
        return input;
    }
    ObjectUtils.deepCopy = deepCopy;
    /** Construct an object of type T and assign data to it */
    function from(target, data, ...args) {
        return Object.assign(new target(...args), data);
    }
    ObjectUtils.from = from;
    /** Construct an object of type T and assign JSON data to it */
    function fromJSON(target, data, ...args) {
        return ObjectUtils.from(target, JSON.parse(data), ...args);
    }
    ObjectUtils.fromJSON = fromJSON;
    const proxies = new WeakMap();
    function $(object, key) {
        let store = proxies.get(object);
        if (store === undefined) {
            proxies.set(object, store = new Map());
        }
        let proxy = store.get(key);
        if (proxy === undefined) {
            proxy = {
                get: () => { return object[key]; },
                set: (variable) => { object[key] = variable; },
                onUpdate: new BasicEvent(),
                onChange: new BasicEvent()
            };
            store.set(key, proxy);
        }
        return proxy;
    }
    ObjectUtils.$ = $;
    function bind() {
        return (target, key) => {
            let descriptor = Object.getOwnPropertyDescriptor(target, key);
            if (!descriptor || (typeof descriptor.value !== "function")) {
                throw new Error(`Bind can only be used on function! ${key} is not a function!`);
            }
            return {
                configurable: true,
                get() {
                    const fnBound = ((...args) => { return descriptor.value.apply(this, args); });
                    Object.defineProperty(this, key, {
                        value: fnBound,
                        configurable: true,
                        writable: true
                    });
                    return fnBound;
                }
            };
        };
    }
    ObjectUtils.bind = bind;
    function mixin(derived, mixed) {
        mixed.forEach(proto => {
            Object.getOwnPropertyNames(proto.prototype).forEach(name => {
                if (name !== "constructor") {
                    let descriptor = Object.getOwnPropertyDescriptor(proto.prototype, name);
                    if (descriptor !== undefined) {
                        Object.defineProperty(derived.prototype, name, descriptor);
                    }
                }
            });
        });
    }
    ObjectUtils.mixin = mixin;
})(ObjectUtils || (ObjectUtils = {}));

var Utils;
(function (Utils) {
    Utils.Object = ObjectUtils;
    Utils.Format = FormatUtils;
    /** Generate a random integer between min and max(inclusive) */
    function randomInt(min, max) {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }
    Utils.randomInt = randomInt;
    /** Generate a random integer between min and max(exclusive) */
    function rangeInt(min, max) {
        min = (min) | 0;
        max = (max) | 0;
        return ((Math.random() * (max - min)) + min) | 0;
    }
    Utils.rangeInt = rangeInt;
    /** Generate a random float between min and max(exclusive) */
    function rangeFloat(min, max) {
        min = +(min);
        max = +(max);
        return +((Math.random() * (max - min)) + min);
    }
    Utils.rangeFloat = rangeFloat;
    function pickArray(array) {
        if (array.length <= 0) {
            return undefined;
        }
        return array[Utils.rangeInt(0, array.length)];
    }
    Utils.pickArray = pickArray;
    function minMax(value, min = 0, max = 1) {
        if (min >= max) {
            throw new Error("[minMax] min >= max");
        }
        return (value > max) ? max : ((value < min) ? min : value);
    }
    Utils.minMax = minMax;
    /** Converts number from one range to another */
    function remapRange(value, sourceMin = 0, sourceMax = 1, targetMin = 0, targetMax = 1) {
        if (sourceMin >= sourceMax) {
            throw new Error("[remapRange] sourceMin >= sourceMax");
        }
        if (value > sourceMax) {
            value = sourceMax;
        }
        if (value < sourceMin) {
            value = sourceMin;
        }
        return (value - sourceMin) * (targetMax - targetMin) / (sourceMax - sourceMin) + targetMin;
    }
    Utils.remapRange = remapRange;
    function sortedIndex(array, value) {
        let low = 0;
        let high = array.length;
        while (low < high) {
            let mid = low + high >>> 1;
            if (array[mid] < value) {
                low = mid + 1;
            }
            else {
                high = mid;
            }
        }
        return low;
    }
    Utils.sortedIndex = sortedIndex;
    function sortedInsert(array, value) {
        let index = Utils.sortedIndex(array, value);
        array.splice(index, 0, value);
    }
    Utils.sortedInsert = sortedInsert;
    function sortedIndexComparator(array, value, comparator) {
        let min = 0;
        let max = array.length;
        let index = (min + max) >>> 1;
        while (max > min) {
            if (comparator(value, array[index]) < 0) {
                max = index;
            }
            else {
                min = index + 1;
            }
            index = (min + max) >>> 1;
        }
        return index;
    }
    Utils.sortedIndexComparator = sortedIndexComparator;
    function sortedInsertComparator(array, value, comparator) {
        let index = Utils.sortedIndexComparator(array, value, comparator);
        array.splice(index, 0, value);
    }
    Utils.sortedInsertComparator = sortedInsertComparator;
    function timeout(delay) {
        return __awaiter(this, void 0, void 0, function* () {
            const lastTime = Date.now();
            return new Promise(resolve => {
                setTimeout((args, ms) => {
                    resolve(Date.now() - lastTime);
                }, delay);
            });
        });
    }
    Utils.timeout = timeout;
    function internalNextTick(cb) {
        if ((typeof process === 'object') && process && (typeof process.nextTick === 'function')) {
            return process.nextTick(cb);
        }
        if (typeof queueMicrotask === "function") {
            return queueMicrotask(cb);
        }
        if (typeof setImmediate === 'function') {
            return setImmediate(cb);
        }
        return setTimeout(cb, 0);
    }
    Utils.internalNextTick = internalNextTick;
    function internalImmediate(cb) {
        if (typeof setImmediate === 'function') {
            return setImmediate(cb);
        }
        return setTimeout(cb, 0);
    }
    Utils.internalImmediate = internalImmediate;
    function immediate(value) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                Utils.internalImmediate(() => resolve(value));
            });
        });
    }
    Utils.immediate = immediate;
    function nextTick(value) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                Utils.internalNextTick(() => resolve(value));
            });
        });
    }
    Utils.nextTick = nextTick;
    function strReplaceMulti(str, replace) {
        let out = "";
        let i = 0;
        while (i < str.length) {
            let replaced = false;
            for (const find in replace) {
                if (replace.hasOwnProperty(find)) {
                    const target = replace[find];
                    if (str.startsWith(find, i)) {
                        out += target;
                        i += find.length;
                        replaced = true;
                        break;
                    }
                }
            }
            if (!replaced) {
                out += str[i];
                i++;
            }
        }
        return out;
    }
    Utils.strReplaceMulti = strReplaceMulti;
    let hidIndex = 0;
    function generateHID() {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let text = "";
        const time = new Date().valueOf();
        for (let i = 0; i < 8; i++) {
            text += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        for (let i = 0; i < 2; i++) {
            text += chars.charAt(Math.floor(i > 0 ? hidIndex / Math.pow(chars.length, i) : hidIndex) % chars.length);
        }
        hidIndex++;
        for (let i = 0; i < 6; i++) {
            text += chars.charAt(Math.floor(i > 0 ? time / Math.pow(chars.length, i) : time) % chars.length);
        }
        return text;
    }
    Utils.generateHID = generateHID;
    let huuidIndex = 0;
    let huuidRNG = new HaxRNG(Date.now());
    function generateHUUID() {
        const time = (Date.now() / 1000) | 0;
        let text = HaxCompressor.compressNumber(time, 6);
        text += HaxCompressor.compressNumber(huuidIndex, 3);
        for (let i = 0; i < 4; i++) {
            text += HaxCompressor.chars.charAt(Math.floor(Math.random() * HaxCompressor.charsLength));
        }
        for (let i = 0; i < 3; i++) {
            text += huuidRNG.nextHChar();
        }
        huuidIndex++;
        return text;
    }
    Utils.generateHUUID = generateHUUID;
    function generateHUUID24() {
        const time = (Date.now() / 1000) | 0;
        let text = HaxCompressor.compressNumber(time, 8);
        text += HaxCompressor.compressNumber(huuidIndex, 3);
        for (let i = 0; i < 6; i++) {
            text += HaxCompressor.chars.charAt(Math.floor(Math.random() * HaxCompressor.charsLength));
        }
        for (let i = 0; i < 7; i++) {
            text += huuidRNG.nextHChar();
        }
        huuidIndex++;
        return text;
    }
    Utils.generateHUUID24 = generateHUUID24;
    Utils.imul = Math.imul != undefined ? Math.imul : (a, b) => {
        let result = (a & 0x003fffff) * b;
        if (a & 0xffc00000)
            result += ((a & 0xffc00000) * b) | 0;
        return result | 0;
    };
    // cyrb53 (public domain)
    function hashString(str) {
        let h1 = 0xdeadbeef;
        let h2 = 0x41c6ce57;
        for (let i = 0, ch; i < str.length; i++) {
            ch = str.charCodeAt(i);
            h1 = Utils.imul(h1 ^ ch, 2654435761);
            h2 = Utils.imul(h2 ^ ch, 1597334677);
        }
        h1 = Utils.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Utils.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Utils.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Utils.imul(h1 ^ (h1 >>> 13), 3266489909);
        return Base85.default.intToString(h2 >>> 0) + Base85.default.intToString(h1 >>> 0);
    }
    Utils.hashString = hashString;
    function equals(a, b, keyIgnore) {
        if (a === b) {
            return true;
        }
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) {
                return false;
            }
            for (let i = 0; i < a.length; i++) {
                if (!Utils.equals(a[i], b[i], keyIgnore)) {
                    return false;
                }
            }
            return true;
        }
        if (!(typeof (a) === "object") || !(typeof (b) === "object") || a === null || b === null) {
            return false;
        }
        if (a.constructor !== b.constructor) {
            return false;
        }
        for (const key in a) {
            if ((keyIgnore && keyIgnore(key))) {
                continue;
            }
            if (!a.hasOwnProperty(key)) {
                continue;
            }
            if (!b.hasOwnProperty(key)) {
                return false;
            }
            if (a[key] === b[key]) {
                continue;
            }
            if (typeof (a[key]) !== "object") {
                return false;
            }
            if (!Utils.equals(a[key], b[key], keyIgnore)) {
                return false;
            }
        }
        for (const key in b) {
            if ((keyIgnore && keyIgnore(key))) {
                continue;
            }
            if (b.hasOwnProperty(key) && !a.hasOwnProperty(key)) {
                return false;
            }
        }
        return true;
    }
    Utils.equals = equals;
    function shuffle(array) {
        let len = array.length;
        for (let i = 0; i < len; i++) {
            let j = (Math.random() * (i + 1)) | 0;
            if (i != j) {
                [array[i], array[j]] = [array[j], array[i]];
            }
        }
    }
    Utils.shuffle = shuffle;
    function randomWeightedObject(object) {
        let totalWeight = 0;
        for (let name in object) {
            if (object.hasOwnProperty(name)) {
                let element = object[name];
                totalWeight += element.weight;
            }
        }
        let randomWeight = Math.random() * totalWeight;
        for (let name in object) {
            if (object.hasOwnProperty(name)) {
                let element = object[name];
                randomWeight -= element.weight;
                if (randomWeight <= 0) {
                    return { key: name, value: element };
                }
            }
        }
        return undefined;
    }
    Utils.randomWeightedObject = randomWeightedObject;
    function randomWeightedArray(object) {
        let totalWeight = 0;
        for (let i = 0; i < object.length; i++) {
            const element = object[i];
            totalWeight += element.weight;
        }
        let randomWeight = Math.random() * totalWeight;
        for (let i = 0; i < object.length; i++) {
            const element = object[i];
            randomWeight -= element.weight;
            if (randomWeight <= 0) {
                return { key: i, value: element };
            }
        }
        return undefined;
    }
    Utils.randomWeightedArray = randomWeightedArray;
    function getOrdinal(num) {
        let s = ["th", "st", "nd", "rd"];
        let v = num % 100;
        return num.toLocaleString("en-US") + (s[(v - 20) % 10] || s[v] || s[0]);
    }
    Utils.getOrdinal = getOrdinal;
    function initArray(size, value) {
        if (typeof value === "function") {
            const arr = [];
            arr.length = size;
            for (let i = 0; i < size; i++) {
                arr[i] = value(i);
            }
            return arr;
        }
        else {
            const arr = [];
            arr.length = size;
            for (let i = 0; i < size; i++) {
                arr[i] = value;
            }
            return arr;
        }
    }
    Utils.initArray = initArray;
    function initArray2(sizeX, sizeY, value) {
        let size = sizeX * sizeY;
        const arr = [];
        arr.length = size;
        for (let y = 0; y < sizeY; y++) {
            for (let x = 0; x < sizeX; x++) {
                let i = y * sizeX + x;
                arr[i] = value(x, y, i);
            }
        }
        return arr;
    }
    Utils.initArray2 = initArray2;
    function initArray3(sizeX, sizeY, sizeZ, value) {
        let size = sizeX * sizeY * sizeZ;
        let sizeXY = sizeX * sizeY;
        const arr = [];
        arr.length = size;
        for (let z = 0; z < sizeZ; z++) {
            for (let y = 0; y < sizeY; y++) {
                for (let x = 0; x < sizeX; x++) {
                    let i = z * sizeXY + y * sizeX + x;
                    arr[i] = value(x, y, z, i);
                }
            }
        }
        return arr;
    }
    Utils.initArray3 = initArray3;
    function normalizeNulls(data, inArray = false) {
        if (typeof (data) === "object") {
            // tslint:disable-next-line:forin
            for (const key in data) {
                let value = data[key];
                if (!inArray && value === null) {
                    data[key] = undefined;
                }
                else if (typeof (value) === "object") {
                    Utils.normalizeNulls(value, Array.isArray(value));
                }
            }
        }
    }
    Utils.normalizeNulls = normalizeNulls;
})(Utils || (Utils = {}));

class ClassVariable {
    constructor(name) {
        this.data = new Map();
        this.name = name;
    }
    register(clazz) {
        this.data.forEach((it) => {
            if (it && typeof it.register === "function") {
                it.register(this);
            }
        });
    }
    static createType(key) {
        let symbol = Symbol.for("ClassVariable::" + key);
        return symbol;
    }
    getData(key) {
        return this.data.get(key);
    }
    attachData(key, value, override) {
        let current = this.data.get(key);
        if (current === undefined || override) {
            this.data.set(key, value);
            return value;
        }
        else {
            throw new Error("[ClassVariable::attachData] Couldn't attach data, data already exists!");
        }
    }
}
class ClassMeta {
    constructor(clazz, prototype) {
        this.registered = false;
        this.registerCallbacks = [];
        this.finalizeCallbacks = [];
        this.variables = new Map();
        if (!clazz) {
            throw new Error("[ClassMeta::constructor] Something went wrong, trying to create a class with no prototype!");
        }
        this.$clazz = clazz;
        this.$prototype = prototype;
        this.parent = Object.getPrototypeOf(prototype).constructor["$ClassMeta"];
    }
    register(singleton) {
        if (this.registered) {
            throw new Error("[ClassMeta::register] Class already registered!");
        }
        this.registered = true;
        this.singleton = singleton;
        this.callRegister(this);
        ClassMeta.registerDecorators.forEach((fn) => { fn(this); });
        this.callFinalize(this);
        ClassMeta.finalizeDecorators.forEach((fn) => { fn(this); });
        this.forEachVariable((variable) => {
            variable.register(this);
        });
    }
    callRegister(target) {
        if (this.parent !== undefined) {
            this.parent.callRegister(target);
        }
        this.registerCallbacks.forEach((fn) => { fn(this); });
    }
    callFinalize(target) {
        if (this.parent !== undefined) {
            this.parent.callFinalize(target);
        }
        this.finalizeCallbacks.forEach((fn) => { fn(this); });
    }
    isRegistered() {
        return this.registered;
    }
    attachSingleton(instance) {
        if (this.singleton === false) {
            throw new Error("[ClassMeta::attachSingleton] This class wasn't registered as singleton class!");
        }
        this.instance = instance;
    }
    getSingleton() {
        if (this.singleton === false) {
            throw new Error("[ClassMeta::attachSingleton] This class wasn't registered as singleton class!");
        }
        return this.instance;
    }
    createVariable(key) {
        let variable = this.variables.get(key);
        if (variable === undefined) {
            variable = new ClassVariable(key);
            this.variables.set(key, variable);
        }
        return variable;
    }
    getVariable(key) {
        let variable = this.variables.get(key);
        if (variable !== undefined) {
            return variable;
        }
        if (this.parent !== undefined) {
            return this.parent.getVariable(key);
        }
        else {
            return undefined;
        }
    }
    forEachVariable(callback) {
        if (this.parent !== undefined) {
            this.parent.forEachVariable(callback);
        }
        this.variables.forEach(callback);
    }
    registerCallback(type, fn) {
        if (type === "Register") {
            this.registerCallbacks.push(fn);
        }
        else if (type === "Finalize") {
            this.finalizeCallbacks.push(fn);
        }
        else {
            throw new Error("[ClassMeta::RegisterCallback] Invalid callback type!");
        }
    }
    static registerDecoratorCallback(type, fn) {
        if (type === "Register") {
            ClassMeta.registerDecorators.push(fn);
        }
        else if (type === "Finalize") {
            ClassMeta.finalizeDecorators.push(fn);
        }
        else {
            throw new Error("[ClassMeta::RegisterCallback] Invalid callback type!");
        }
    }
}
ClassMeta.registerDecorators = [];
ClassMeta.finalizeDecorators = [];
function GetClassMeta(target, create) {
    if (!target || !target.constructor) {
        throw new Error("[GetClassMeta] Something went wrong, invalid target!");
    }
    if (!target.constructor.hasOwnProperty("$ClassMeta")) {
        if (!create) {
            return undefined;
        }
        const classMeta = new ClassMeta(target.constructor, target);
        target.constructor["$ClassMeta"] = classMeta;
        return classMeta;
    }
    else {
        return target.constructor["$ClassMeta"];
    }
}
function RegisterClass(target, singleton) {
    let meta = GetClassMeta(target.prototype, true);
    meta.register(singleton);
    return meta;
}
function RegisterClassVariable(target, key) {
    let meta = GetClassMeta(target.prototype, true);
    return meta.createVariable(key);
}

const Services = {};
function Service(name) {
    return function (target) {
        name = (name !== undefined) ? name : target.name;
        if (Services[name]) {
            throw new Error("Couldn't register " + target.name + " with name " + name + " as Service! Another service with that name is already registered!");
        }
        target.singleton = true;
        target.instance = undefined;
        Services[name] = target;
        if (!target.createService) {
            throw new Error("Service doesn't have CreateService function on " + target.name + " with name " + name + "!");
        }
        let serviceCreatedEvent = new BasicEvent();
        target.onServiceCreated = serviceCreatedEvent;
        let classMeta = RegisterClass(target, true);
        serviceCreatedEvent.addCallback((instance) => {
            classMeta.attachSingleton(instance);
        });
    };
}
class ServiceClass {
    constructor() {
        this.onStartedEvent = new BasicEvent();
        this.onStoppedEvent = new BasicEvent();
        this.active = false;
        this.__starting = false;
        this.__stopping = false;
    }
    static createService() {
        const instance = new this();
        let clazz = Object.getPrototypeOf(instance).constructor;
        if (!clazz.hasOwnProperty("singleton")) {
            throw new Error("Can't attach instance of service! Service not registered correctly!");
        }
        // make sure there are no singletons already attached
        while (clazz.hasOwnProperty("singleton")) {
            if (clazz.hasOwnProperty("instance") && clazz.instance !== undefined) {
                throw new Error("Can't attach instance of " + Object.getPrototypeOf(instance).constructor.name + " service! " + clazz.name + " already attached!");
            }
            clazz = Object.getPrototypeOf(clazz);
        }
        clazz = Object.getPrototypeOf(instance).constructor;
        while (clazz.hasOwnProperty("singleton")) {
            clazz.instance = instance;
            clazz = Object.getPrototypeOf(clazz);
        }
        Object.getPrototypeOf(instance).constructor.onServiceCreated.execute(instance);
        return instance;
    }
    static get valid() {
        if (this.instance) {
            return this.instance.active;
        }
        return false;
    }
    /**
     * Only returns the instance when it's valid (enabled and active)
     */
    static getInstance() {
        if (this.instance && this.instance.active) {
            return this.instance;
        }
        return undefined;
    }
    setActive(active) {
        this.active = active;
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.__starting === true || this.__stopping === true) {
                return false;
            }
            this.__starting = true;
            console.log("Starting service " + Object.getPrototypeOf(this).constructor.name);
            let ret;
            try {
                ret = yield this.onStart();
            }
            catch (err) {
                console.log("Failed to start service " + Object.getPrototypeOf(this).constructor.name + ": force stopping", err);
                try {
                    yield this.onStop();
                }
                catch (err) {
                    // ignore
                }
                this.__starting = false;
                this.__stopping = false;
                this.setActive(false);
                return false;
            }
            this.setActive(true);
            this.onStartedEvent.execute();
            this.__starting = false;
            console.log("Started service " + Object.getPrototypeOf(this).constructor.name);
            return ret;
        });
    }
    stop() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.__starting === true || this.__stopping === true) {
                return false;
            }
            this.__stopping = true;
            console.log("Stopping service " + Object.getPrototypeOf(this).constructor.name);
            this.setActive(false);
            let ret;
            try {
                ret = yield this.onStop();
            }
            catch (err) {
                console.log("Error stopping service " + Object.getPrototypeOf(this).constructor.name, err);
                this.__stopping = false;
                return false;
            }
            this.onStoppedEvent.execute();
            this.__stopping = false;
            console.log("Stopped service " + Object.getPrototypeOf(this).constructor.name);
            return ret;
        });
    }
    onStart() {
        return __awaiter(this, void 0, void 0, function* () { return true; });
    }
    onStop() {
        return __awaiter(this, void 0, void 0, function* () { return true; });
    }
    restart() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.__starting === true || this.__stopping === true) {
                return false;
            }
            if (this.active && !(yield this.stop())) {
                return false;
            }
            if (this.active) {
                throw new Error("Service still active after stopping!");
            }
            yield Utils.timeout(1);
            if (!(yield this.start())) {
                return false;
            }
            if (!this.active) {
                throw new Error("Service still not active after starting!");
            }
            return true;
        });
    }
}
ServiceClass.dependencies = [];

class UniqueArray extends Array {
    constructor(...items) { super(...items); }
    get(index) { return this[index]; }
    map(callback, thisArg) {
        return Array.from(this, (v, i) => callback.call(thisArg, v, i, this));
    }
    copy() {
        return new UniqueArray(...this);
    }
    concat(...items) {
        // TODO: improve?
        return this.copy().concat(...items).filter((v, i, self) => self.lastIndexOf(v) === i);
    }
    push(...items) {
        if (items.length > 1) {
            items = items.filter((value, index, self) => self.lastIndexOf(value) === index);
        }
        for (const item of items) {
            const at = this.indexOf(item);
            if (at > -1) {
                super.splice(at, 1);
            }
        }
        return super.push(...items);
    }
    unshift(...items) {
        if (items.length > 1) {
            items = items.filter((value, index, self) => self.lastIndexOf(value) === index);
        }
        for (const item of items) {
            const at = this.indexOf(item);
            if (at > -1) {
                super.splice(at, 1);
            }
        }
        return super.unshift(...items);
    }
    /**
     * Removes the last element from an array and returns it.
     * If the array is empty, undefined is returned and the array is not modified.
     */
    // pop(): T | undefined;
    /**
     * Removes the first element from an array and returns it.
     * If the array is empty, undefined is returned and the array is not modified.
     */
    // shift(): T | undefined;
    insertAt(index, ...items) {
        if (items.length > 1) {
            items = items.filter((value, index, self) => self.lastIndexOf(value) === index);
        }
        for (const item of items) {
            const at = this.indexOf(item);
            if (at > -1) {
                super.splice(at, 1);
                if (at < index) {
                    index--;
                }
            }
        }
        super.splice(index, 0, ...items);
    }
    removeElement(element) {
        const at = this.indexOf(element);
        if (at > -1) {
            super.splice(at, 1);
            return true;
        }
        return false;
    }
    removeAt(index) {
        if (index < this.length) {
            super.splice(index, 1);
            index--;
            return true;
        }
        return false;
    }
    clear() {
        super.splice(0, this.length);
    }
    /** @deprecated This function doesn't work with UniqueArray (or it's usage would be confusing) */
    fill() { throw new Error("Intentionally disabled"); }
    /** @deprecated This function doesn't work with UniqueArray (or it's usage would be confusing) */
    copyWithin() { throw new Error("Intentionally disabled"); }
    splice() { throw new Error("Intentionally disabled"); }
}

class MapWithDefault extends Map {
    constructor(createDefault) {
        super();
        this.createDefault = createDefault;
    }
    get(key) {
        let value = super.get(key);
        if (value === undefined) {
            value = this.createDefault(key);
            super.set(key, value);
        }
        return value;
    }
    getOrError(key) {
        let value = super.get(key);
        if (value === undefined) {
            throw new Error();
        }
        return value;
    }
    getOrUndefined(key) {
        return super.get(key);
    }
}

class DataProviderVariable {
    register(variable) {
        this.name = variable.name;
    }
}
const VARIABLE = ClassVariable.createType("DataProviderVariable");
/*
let test_var = new ClassVariable("a");
let data = test_var.getData(VARIABLE);
data;
data = test_var.attachData(VARIABLE, new DataProviderVariable());
data.varA = "test";
data.varB = 123;
data;
data = test_var.getData(VARIABLE);
data;
*/
function Class() {
    return function (target) {
        let classMeta = RegisterClass(target, true);
    };
}
class DataProvider {
    static variable() {
        return function (target, key) {
            let variable = RegisterClassVariable(target.constructor, key).attachData(VARIABLE, new DataProviderVariable());
        };
    }
}
/*
@Class()
class TestAProvider {

    @DataProvider.variable()
    varA: string;

    @DataProvider.variable()
    varB: number;

}

let varAData = GetClassMeta(TestAProvider.prototype, false)!.getVariable("varA")!.getData(VARIABLE);
varAData;

let varBData = GetClassMeta(TestAProvider.prototype, false)!.getVariable("varB")!.getData(VARIABLE);
varBData;
*/

class Base64 {
    constructor(map) {
        if (map.length !== 64) {
            throw new Error();
        }
        let chars = [];
        for (let i = 0; i < map.length; i++) {
            chars.push(map.charAt(i));
        }
        let rev = {};
        chars.forEach((c, i) => { rev[c] = i; });
        this.chars = chars;
        this.rev = rev;
    }
    encodeString(text, padding = false) {
        return this.encode(BinaryUtils.stringToUint8Array(text), padding);
    }
    encode(array, padding = false) {
        let chars = this.chars;
        let out = "";
        let length = array.length;
        let groups = Math.floor(array.length / 3);
        for (let g = 0; g < groups; g++) {
            let number = 0;
            for (let i = 0; i < 3; i++) {
                number = (number * 256) + array[g * 3 + i];
            }
            let divider = 64 * 64 * 64;
            for (let i = 0; i < 4; i++) {
                out += chars[((number / divider) >>> 0) % 64];
                divider /= 64;
            }
        }
        let lastIndex = groups * 3;
        let lastSize = length - lastIndex;
        if (lastSize !== 0) {
            let number = 0;
            for (let i = 0; i < lastSize; i++) {
                number = (number * 256) + array[lastIndex + i];
            }
            let divider = 64;
            if (lastSize === 2) {
                divider = 64 * 64;
                number <<= 2;
            }
            if (lastSize === 1) {
                number <<= 4;
            }
            for (let i = 0; i < lastSize + 1; i++) {
                out += chars[((number / divider) >>> 0) % 64];
                divider /= 64;
            }
            if (padding === true) {
                for (let i = 0; i < 3 - lastSize; i++) {
                    out += "=";
                }
            }
        }
        return out;
    }
    decodeString(text) {
        return BinaryUtils.uint8ToStringArray(this.decode(text));
    }
    decode(array) {
        let rev = this.rev;
        let length = array.length;
        if (array[length - 1] === "=") {
            length -= 1;
        }
        if (array[length - 1] === "=") {
            length -= 1;
        }
        if (array[length - 1] === "=") {
            length -= 1;
        }
        let groups = Math.floor(length / 4);
        let lastIndex = groups * 4;
        let lastSize = length - lastIndex;
        let out = new Uint8Array(groups * 3 + (lastSize > 1 ? lastSize - 1 : 0));
        let index = 0;
        for (let g = 0; g < groups; g++) {
            let number = 0;
            for (let i = 0; i < 4; i++) {
                number = number * 64 + rev[array[g * 4 + i]];
            }
            let divider = 256 * 256;
            for (let i = 0; i < 3; i++) {
                out[index++] = ((number / divider) >>> 0) % 256;
                divider /= 256;
            }
        }
        if (lastSize !== 0) {
            let number = 0;
            for (let i = 0; i < lastSize; i++) {
                number = number * 64 + rev[array[(lastIndex) + i]];
            }
            let divider = 1;
            if (lastSize === 3) {
                divider = 256;
                number >>= 2;
            }
            if (lastSize === 2) {
                number >>= 4;
            }
            for (let i = 0; i < lastSize - 1; i++) {
                out[index++] = ((number / divider) >>> 0) % 256;
                divider /= 256;
            }
        }
        return out;
    }
    packObject(data) {
        return this.encodeString(JSON.stringify(data));
    }
    unpackObject(data) {
        return JSON.parse(this.decodeString(data));
    }
}
Base64.default = new Base64("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/");
Base64.web = new Base64("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_");

function now() {
    let hrTime = process.hrtime();
    return hrTime[0] * 1000 + hrTime[1] / 1000000.0;
}
class Benchmark {
    static benchmark(func, iterations) {
        const starTime = now();
        func(iterations);
        const endTime = now();
        return endTime - starTime;
    }
    static benchmarkFull(tests, warmups, iterations, multiplier = 1, loops = 5) {
        const results = {};
        for (const testName in tests) {
            if (tests.hasOwnProperty(testName)) {
                const test = tests[testName];
                results[testName] = { code: test.code.trim(), results: [] };
                // warmup
                Benchmark.benchmark(test.func, warmups);
            }
        }
        for (let i = 0; i < loops; i++) {
            for (const testName in tests) {
                if (tests.hasOwnProperty(testName)) {
                    const test = tests[testName];
                    const time = Benchmark.benchmark(test.func, iterations);
                    results[testName].results.push(time);
                    console.log(testName, time);
                }
            }
        }
        return {
            "iterations": iterations,
            "multiplier": multiplier,
            "results": results
        };
    }
}

class Color {
    constructor(r, g, b, a) {
        this.r = r | 0;
        this.g = g | 0;
        this.b = b | 0;
        this.a = a | 0;
    }
    copy() {
        return new Color(this.r, this.g, this.b, this.a);
    }
    static fromHSL(h, s, l, a) {
        let r, g, b;
        if (s === 0) {
            r = g = b = l; // achromatic
        }
        else {
            const hue2rgb = (x, y, t) => {
                if (t < 0) {
                    t += 1;
                }
                if (t > 1) {
                    t -= 1;
                }
                if (t < 1 / 6) {
                    return x + (y - x) * 6 * t;
                }
                if (t < 1 / 2) {
                    return y;
                }
                if (t < 2 / 3) {
                    return x + (y - x) * (2 / 3 - t) * 6;
                }
                return x;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        return new Color((r * 255) | 0, (g * 255) | 0, (b * 255) | 0, a);
    }
    mix(other, t) {
        return new Color((this.r * t) + (other.r * (1 - t)) | 0, (this.g * t) + (other.g * (1 - t)) | 0, (this.b * t) + (other.b * (1 - t)) | 0, (this.a * t) + (other.a * (1 - t)) | 0);
    }
    toHTML() {
        return `rgba(${this.r}, ${this.g}, ${this.b}, ${(this.a / 255).toFixed(4)})`;
    }
}

class ColorUtils {
    static hslToHex(h, s, l) {
        let r, g, b;
        if (s === 0) {
            r = g = b = l; // achromatic
        }
        else {
            const hue2rgb = (x, y, t) => {
                if (t < 0) {
                    t += 1;
                }
                if (t > 1) {
                    t -= 1;
                }
                if (t < 1 / 6) {
                    return x + (y - x) * 6 * t;
                }
                if (t < 1 / 2) {
                    return y;
                }
                if (t < 2 / 3) {
                    return x + (y - x) * (2 / 3 - t) * 6;
                }
                return x;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        const hexChars = "0123456789ABCDEF".split("");
        function hex(x) { return hexChars[(x / 16) | 0] + hexChars[(x % 16) | 0]; }
        return `#${hex(((r * 255) | 0))}${hex(((g * 255) | 0))}${hex(((b * 255) | 0))}`;
    }
}

class ErrorResponse extends Error {
    constructor(message, code = 0) {
        super(message);
        this.code = code;
        this.error = message;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

class InternalError extends Error {
    constructor(message, code = 0) {
        super(message);
        this.code = code;
        this.error = message;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

// 41, 43, 47, 53, 59, 61, 67, 71, 73, 79, 83, 89, 97
const keySizeA = 47;
const keySizeB = 61;
const keySizeC = 83;
class HaxEncryptor3 {
    constructor(keyA, keyB, keyC) {
        this.encA = new Uint8Array(256 * 256);
        this.decA = new Uint8Array(256 * 256);
        this.encB = new Uint8Array(256 * 256);
        this.decB = new Uint8Array(256 * 256);
        this.encC = new Uint8Array(256 * 256);
        this.decC = new Uint8Array(256 * 256);
        this.randomize(this.encA, this.decA, HaxEncryptor3.stringHash(keyA));
        this.randomize(this.encB, this.decB, HaxEncryptor3.stringHash(keyB));
        this.randomize(this.encC, this.decC, HaxEncryptor3.stringHash(keyC));
        this.keyA = this.stringHashArray(keyA, keySizeA);
        this.keyB = this.stringHashArray(keyB, keySizeB);
        this.keyC = this.stringHashArray(keyC, keySizeC);
        this.keyHash = HaxEncryptor3.calculateHash(keyA, keyB, keyC);
    }
    static calculateHash(keyA, keyB, keyC) {
        let keyHash = HaxEncryptor3.stringHash(keyA + keyB + keyC);
        keyHash = HaxEncryptor3.stringHash("Special0CaloriesSalt" + keyHash);
        keyHash = HaxEncryptor3.stringHash("EmptyCalories" + keyHash);
        keyHash = HaxEncryptor3.stringHash("ImpossibleSalt" + keyHash);
        return keyHash;
    }
    randomize(encArray, decArray, seed) {
        const rngA = new HaxRNG(seed | 0);
        const rngB = new HaxRNG((seed + rngA.safeUInt() + 666) | 0);
        const rngC = new HaxRNG((seed + rngA.safeUInt() + 1337) | 0);
        for (let i = 0; i < 256; i++) {
            for (let j = 0; j < 256; j++) {
                encArray[(i * 256) + j] = j;
            }
            for (let j = 0; j < 256; j++) {
                const r2 = rngA.nextUByte(), tmp = encArray[(i * 256) + j];
                encArray[(i * 256) + j] = encArray[(i * 256) + r2];
                encArray[(i * 256) + r2] = tmp;
            }
            for (let j = 0; j < 256; j++) {
                const r1 = rngB.nextUByte(), tmp = encArray[(i * 256) + r1];
                encArray[(i * 256) + r1] = encArray[(i * 256) + j];
                encArray[(i * 256) + j] = tmp;
            }
            for (let j = 0; j < (4 * 256); j++) {
                const r1 = rngC.nextUByte(), r2 = rngC.nextUByte(), tmp = encArray[(i * 256) + r1];
                encArray[(i * 256) + r1] = encArray[(i * 256) + r2];
                encArray[(i * 256) + r2] = tmp;
            }
            for (let j = 0; j < 256; j++) {
                const r1 = rngA.nextUByte(), r2 = rngB.nextUByte(), tmp = encArray[(i * 256) + r1];
                encArray[(i * 256) + r1] = encArray[(i * 256) + r2];
                encArray[(i * 256) + r2] = tmp;
            }
        }
        for (let i = 0; i < 256; i++) {
            for (let j = 0; j < 256; j++) {
                decArray[(i * 256) + (encArray[(i * 256) + j])] = j;
            }
        }
    }
    stringHashArray(str, size) {
        str = str + "$HaxEncryptor$" + str + "$HaxEncryptor$" + str;
        const length = str.length;
        const outBuffer = new Uint8Array(size);
        // outBuffer
        let hashCode = 0;
        for (let i = 0; i < size; i++) {
            hashCode += i;
            for (let j = i; j < i + 8; j++) {
                hashCode = (31 * hashCode + str.charCodeAt(j % length)) << 0;
            }
            outBuffer[i] = (hashCode | 0);
        }
        return outBuffer;
    }
    static stringHash(str) {
        str = str + "$HaxEncryptor$" + str + "$HaxEncryptor$" + str;
        let hashCode = 0;
        for (let i = 0; i < str.length; i++) {
            hashCode = (31 * hashCode + str.charCodeAt(i)) << 0;
        }
        return hashCode;
    }
    encrypt(buffer, keyOffset) {
        const size = buffer.byteLength;
        const outBuffer = new Uint8Array(buffer.byteLength);
        for (let i = 0; i < size; i++) {
            outBuffer[i] = this.encryptByte(buffer[i], keyOffset + i);
        }
        return outBuffer;
    }
    encryptByte(input, keyOffset) {
        input = input | 0;
        input = (this.encA[this.keyA[keyOffset % keySizeA] * 256 + input] | 0);
        input = (this.encB[this.keyB[keyOffset % keySizeB] * 256 + input] | 0);
        input = (this.encC[this.keyC[keyOffset % keySizeC] * 256 + input] | 0);
        return input;
    }
    decrypt(buffer, keyOffset) {
        const size = buffer.byteLength;
        const outBuffer = new Uint8Array(buffer.byteLength);
        for (let i = 0; i < size; i++) {
            outBuffer[i] = this.decryptByte(buffer[i], keyOffset + i);
        }
        return outBuffer;
    }
    decryptByte(input, keyOffset) {
        input = input | 0;
        input = (this.decC[this.keyC[keyOffset % keySizeC] * 256 + input] | 0);
        input = (this.decB[this.keyB[keyOffset % keySizeB] * 256 + input] | 0);
        input = (this.decA[this.keyA[keyOffset % keySizeA] * 256 + input] | 0);
        return input;
    }
}

/** ImprovedPerlin */
class Noise {
    static noise3(x, y, z) {
        let X = Noise.floor(x) & 255;
        let Y = Noise.floor(y) & 255;
        let Z = Noise.floor(z) & 255;
        x -= Noise.floor(x);
        y -= Noise.floor(y);
        z -= Noise.floor(z);
        let u = Noise.fade(x);
        let v = Noise.fade(y);
        let w = Noise.fade(z);
        const p = Noise.p;
        let A = p[X] + Y;
        let B = p[X + 1] + Y;
        let AA = p[A] + Z;
        let AB = p[A + 1] + Z;
        let BA = p[B] + Z;
        let BB = p[B + 1] + Z;
        return Noise.lerp(w, Noise.lerp(v, Noise.lerp(u, Noise.grad3(p[AA], x, y, z), Noise.grad3(p[BA], x - 1, y, z)), Noise.lerp(u, Noise.grad3(p[AB], x, y - 1, z), Noise.grad3(p[BB], x - 1, y - 1, z))), Noise.lerp(v, Noise.lerp(u, Noise.grad3(p[AA + 1], x, y, z - 1), Noise.grad3(p[BA + 1], x - 1, y, z - 1)), Noise.lerp(u, Noise.grad3(p[AB + 1], x, y - 1, z - 1), Noise.grad3(p[BB + 1], x - 1, y - 1, z - 1))));
    }
    static noise2(x, y) {
        let fX = Noise.floor(x);
        let fY = Noise.floor(y);
        let X = fX & 255;
        let Y = fY & 255;
        x -= fX;
        y -= fY;
        let u = Noise.fade(x);
        let v = Noise.fade(y);
        let A = Noise.p[X] + Y;
        let AA = Noise.p[A];
        let AB = Noise.p[A + 1];
        let B = Noise.p[X + 1] + Y;
        let BA = Noise.p[B];
        let BB = Noise.p[B + 1];
        return Noise.lerp(v, Noise.lerp(u, Noise.grad2(Noise.p[AA], x, y), Noise.grad2(Noise.p[BA], x - 1, y)), Noise.lerp(u, Noise.grad2(Noise.p[AB], x, y - 1), Noise.grad2(Noise.p[BB], x - 1, y - 1)));
    }
    static fade(t) {
        return t * t * t * (t * (t * 6 - 15) + 10);
    }
    static lerp(t, a, b) {
        return a + t * (b - a);
    }
    static grad3(hash, x, y, z) {
        let h = hash & 15;
        let u = h < 8 ? x : y;
        let v = h < 4 ? y : h === 12 || h === 14 ? x : z;
        return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
    }
    static grad2(hash, x, y) {
        let h = hash & 11;
        let u = h < 8 ? x : y;
        let v = h < 4 ? y : h === 12 || h === 14 ? x : 0.0;
        return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
    }
    static floor(x) {
        return x > 0 ? x | 0 : (x | 0) - 1;
    }
}
Noise.p = [
    151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36, 103, 30, 69, 142, 8, 99, 37, 240,
    21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94, 252, 219, 203, 117, 35, 11, 32, 57, 177, 33, 88,
    237, 149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165, 71, 134, 139, 48, 27, 166, 77, 146, 158, 231, 83,
    111, 229, 122, 60, 211, 133, 230, 220, 105, 92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216,
    80, 73, 209, 76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86, 164, 100, 109, 198, 173, 186,
    3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38, 147, 118, 126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58,
    17, 182, 189, 28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101, 155, 167, 43, 172, 9,
    129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232, 178, 185, 112, 104, 218, 246, 97, 228, 251, 34, 242, 193,
    238, 210, 144, 12, 191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49, 192, 214, 31, 181, 199, 106, 157,
    184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254, 138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128,
    195, 78, 66, 215, 61, 156, 180, 151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36,
    103, 30, 69, 142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94, 252, 219, 203,
    117, 35, 11, 32, 57, 177, 33, 88, 237, 149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165, 71, 134, 139,
    48, 27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230, 220, 105, 92, 41, 55, 46, 245, 40, 244, 102,
    143, 54, 65, 25, 63, 161, 1, 216, 80, 73, 209, 76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159,
    86, 164, 100, 109, 198, 173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38, 147, 118, 126, 255, 82, 85, 212,
    207, 206, 59, 227, 47, 16, 58, 17, 182, 189, 28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221,
    153, 101, 155, 167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232, 178, 185, 112, 104, 218,
    246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12, 191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49,
    192, 214, 31, 181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254, 138, 236, 205, 93, 222,
    114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215, 61, 156, 180
];

class StringUtils {
    static splitChar(str, char, limit) {
        let last = 0;
        let i = 0;
        const parts = [];
        limit = limit !== undefined ? limit : Number.POSITIVE_INFINITY;
        while (str.length > i && limit > 1) {
            if (str[i] === char) {
                parts.push(str.substring(last, i));
                last = i + 1;
                limit--;
            }
            i++;
        }
        if (last < str.length) {
            parts.push(str.substring(last, str.length));
        }
        else if (last === i) {
            parts.push("");
        }
        return parts;
    }
    static replaceChars(str, chars) {
        let last = 0;
        let i = 0;
        const parts = [];
        while (str.length > i) {
            const replace = chars[str[i]];
            if (replace !== undefined) {
                parts.push(str.substring(last, i));
                parts.push(replace);
                last = i + 1;
            }
            i++;
        }
        if (last < str.length) {
            parts.push(str.substring(last, str.length));
        }
        return parts.join("");
    }
    static findAndReplaceAll(source, find, replace, flags = "") {
        return source.replace(new RegExp(find, flags + "g"), replace);
    }
    // Function that count occurrences of a substring in a string;
    // Source: http://stackoverflow.com/questions/4009756/how-to-count-string-occurrence-in-string/7924240#7924240
    static occurrences(source, find, allowOverlapping = false) {
        source += "";
        find += "";
        if (find.length <= 0) {
            return (source.length + 1);
        }
        let n = 0;
        let pos = 0;
        let step = allowOverlapping ? 1 : find.length;
        while (true) {
            pos = source.indexOf(find, pos);
            if (pos >= 0) {
                ++n;
                pos += step;
            }
            else {
                break;
            }
        }
        return n;
    }
}

class TextUtils {
    static toStringMapping(args) {
        const map = {};
        args.forEach((arg) => { map[arg] = true; });
        return map;
    }
    static getLetterData(char) {
        let isLowerLetter = !!TextUtils.lowerLetters[char];
        let isUpperLetter = !!TextUtils.upperLetters[char];
        let isLetter = isLowerLetter || isUpperLetter;
        return {
            isLowerLetter,
            isUpperLetter,
            isLetter
        };
    }
    static cleanWord(str) {
        let last = 0;
        let i = 0;
        const parts = [];
        while (str.length > i) {
            if (!TextUtils.getLetterData(str[i]).isLetter) {
                parts.push(str.substring(last, i));
                last = i + 1;
            }
            i++;
        }
        if (last < str.length) {
            parts.push(str.substring(last, str.length));
        }
        return parts.join("");
    }
    static splitToWords(str) {
        let parts = [];
        { // split using spaces
            let lastSplit = 0;
            let i = 0;
            let last = TextUtils.getLetterData(str[0]);
            while (str.length > i) {
                let char = str[i];
                let current = TextUtils.getLetterData(char);
                if (char === " ") {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i + 1;
                }
                i++;
                last = current;
            }
            if (lastSplit < str.length) {
                parts.push(str.substring(lastSplit, str.length));
            }
        }
        { // split TextTTextText -> Tex TText Text
            let lastSplit = 0;
            let i = 0;
            let last = TextUtils.getLetterData(str[0]);
            while (str.length > i) {
                let char = str[i];
                let current = TextUtils.getLetterData(char);
                if (char === " " || !current.isLetter) {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i + 1;
                }
                else if (current.isUpperLetter && last.isLowerLetter) {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i;
                }
                i++;
                last = current;
            }
            if (lastSplit < str.length) {
                parts.push(str.substring(lastSplit, str.length));
            }
        }
        { // split TextTTextText -> Textt Text Text
            let lastSplit = 0;
            let i = 0;
            let last = TextUtils.getLetterData(str[0]);
            while (str.length > i) {
                let char = str[i];
                let current = TextUtils.getLetterData(char);
                if (char === " " || !current.isLetter) {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i + 1;
                }
                else if (i > 1 && current.isLowerLetter && last.isUpperLetter) {
                    parts.push(str.substring(lastSplit, i - 1));
                    lastSplit = i - 1;
                }
                i++;
                last = current;
            }
            if (lastSplit < str.length) {
                parts.push(str.substring(lastSplit, str.length));
            }
        }
        {
            let lastSplit = 0;
            let i = 0;
            let last = TextUtils.getLetterData(str[0]);
            while (str.length > i) {
                let char = str[i];
                let current = TextUtils.getLetterData(char);
                if (char === " " || !current.isLetter) {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i + 1;
                }
                else if (i > 1 && last.isUpperLetter && current.isLowerLetter) {
                    parts.push(str.substring(lastSplit, i - 1));
                    lastSplit = i - 1;
                }
                i++;
                last = current;
            }
            if (lastSplit < str.length) {
                parts.push(str.substring(lastSplit, str.length));
            }
        }
        {
            let lastSplit = 0;
            let i = 0;
            let last = TextUtils.getLetterData(str[0]);
            while (str.length > i) {
                let char = str[i];
                let current = TextUtils.getLetterData(char);
                if (char === " " || !current.isLetter) {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i + 1;
                }
                else if (i > 0 && last.isUpperLetter !== current.isUpperLetter) {
                    parts.push(str.substring(lastSplit, i));
                    lastSplit = i;
                }
                i++;
                last = current;
            }
            if (lastSplit < str.length) {
                parts.push(str.substring(lastSplit, str.length));
            }
        }
        let out = [];
        parts.forEach((word) => {
            word = word.toLowerCase();
            if (word.length <= 1) { // 1 character words
                return;
            }
            if (out.indexOf(word) === -1) {
                out.push(word);
            }
            let cleanWord = TextUtils.cleanWord(word);
            if (cleanWord !== word) {
                if (out.indexOf(cleanWord) === -1) {
                    out.push(cleanWord);
                }
            }
            // 1337 -> leet
        });
        return out;
    }
}
TextUtils.lowerLetters = TextUtils.toStringMapping("abcdefghijklmnopqrstuvwxyz".split(""));
TextUtils.upperLetters = TextUtils.toStringMapping("ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""));
TextUtils.digits = TextUtils.toStringMapping("0123456789".split(""));
TextUtils.letters = Object.assign(Object.assign({}, TextUtils.lowerLetters), TextUtils.upperLetters);

class Time {
    static getReadableTime(timeToPrintMS) {
        const second = 1000;
        const minute = 60 * second;
        const hour = 60 * minute;
        const day = 24 * hour;
        const month = 30 * day; // approximately
        const year = 365 * day; // approximately
        const time = [{ year }, { month }, { day }, { hour }, { minute }, { second }].map((item, i, a) => {
            const unitName = Object.keys(item)[0];
            const units = timeToPrintMS / item[unitName] | 0;
            timeToPrintMS -= item[unitName] * units;
            const maybePlural = units === 1 ? "" : "s";
            return units > 0 ? units + " " + unitName + maybePlural : "";
        }).filter(x => x);
        const formattedTime = time.length > 1 ? [...time.slice(0, -1), "and", time.slice(-1)].join(" ") : time[0];
        return formattedTime;
    }
    static getTimeBreakdown(timeToPrintMS) {
        const seconds = 1000;
        const minutes = 60 * seconds;
        const hours = 60 * minutes;
        const days = 24 * hours;
        const months = 30 * days; // approximately
        const years = 365 * days; // approximately
        let retObj = {};
        [{ years }, { months }, { days }, { hours }, { minutes }, { seconds }].forEach((item, i, a) => {
            const unitName = Object.keys(item)[0];
            const units = timeToPrintMS / item[unitName] | 0;
            timeToPrintMS -= item[unitName] * units;
            retObj[unitName] = (units > 0) ? units : 0;
        });
        return retObj;
    }
    static getTimeBreakdownHMS(timeToPrintMS) {
        const seconds = 1000;
        const minutes = 60 * seconds;
        const hours = 60 * minutes;
        let retObj = {};
        [{ hours }, { minutes }, { seconds }].forEach((item, i, a) => {
            const unitName = Object.keys(item)[0];
            const units = timeToPrintMS / item[unitName] | 0;
            timeToPrintMS -= item[unitName] * units;
            retObj[unitName] = (units > 0) ? units : 0;
        });
        return retObj;
    }
    static calculateDifference(then, now) {
        return this.getReadableTime(now.getTime() - then.getTime());
    }
    static getTimeMDY(time) {
        let dateObj;
        if (time) {
            dateObj = new Date(time);
        }
        else {
            dateObj = new Date();
        }
        let month = dateObj.getUTCMonth() + 1; // months from 1-12
        let day = dateObj.getUTCDate();
        let year = dateObj.getUTCFullYear();
        let newDate = month + "/" + day + "/" + year;
        return newDate;
    }
    static getTimestamp(time) {
        let date;
        if (time) {
            date = new Date(time);
        }
        else {
            date = new Date();
        }
        let year = date.getFullYear();
        let month = ("0" + (date.getMonth() + 1)).substr(-2);
        let day = ("0" + date.getDate()).substr(-2);
        let hours = ("0" + date.getHours()).substr(-2);
        let minutes = ("0" + date.getMinutes()).substr(-2);
        let seconds = ("0" + date.getSeconds()).substr(-2);
        return "" + year + "-" + month + "-" + day + "_" + hours + "-" + minutes + "-" + seconds;
    }
    static calculateMilliseconds(hours, minutes, seconds) {
        let ms = hours * this.HOUR_MS;
        if (minutes !== undefined) {
            ms += minutes * this.MINUTE_MS;
        }
        if (seconds !== undefined) {
            ms += seconds * this.SECOND_MS;
        }
        return ms;
    }
    static asParts(timeOrDate, utc = false) {
        let date = (typeof (timeOrDate) === "number") ? new Date(timeOrDate) : timeOrDate;
        let month = ((utc === true) ? date.getUTCMonth() : date.getMonth()) + 1; // months from 1-12
        let day = ((utc === true) ? date.getUTCDate() : date.getDate());
        let year = ((utc === true) ? date.getUTCFullYear() : date.getFullYear());
        let hours = ((utc === true) ? date.getUTCHours() : date.getHours());
        let minutes = ((utc === true) ? date.getUTCMinutes() : date.getMinutes());
        let seconds = ((utc === true) ? date.getUTCSeconds() : date.getSeconds());
        let ms = ((utc === true) ? date.getUTCMilliseconds() : date.getMilliseconds());
        return {
            d: day, day: day,
            dd: ("0" + day).substr(-2),
            m: month, month: month,
            mm: ("0" + month).substr(-2),
            yyyy: year, year: year,
            h: hours, hours: hours,
            hh: ("0" + hours).substr(-2),
            min: minutes, minutes: minutes,
            mmin: ("0" + minutes).substr(-2),
            s: seconds, seconds: seconds,
            ss: ("0" + seconds).substr(-2),
            ms: ms, milliseconds: ms,
            msms: ("00" + ms).substr(-3),
            monthName: Time.MonthNames[month - 1]
        };
    }
}
Time.HOUR_MS = 60 * 60 * 1000;
Time.MINUTE_MS = 60 * 1000;
Time.SECOND_MS = 1000;
Time.MonthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const MetaSymbol = Symbol.for("$Meta");
class BaseClass {
    constructor() {
        this.setupClass();
    }
    setupClass() {
        let meta = GetClassMeta(this, true);
        this[MetaSymbol] = meta;
        if (meta.isRegistered() === false) {
            meta.register(false);
        }
    }
    static Mixin(Base) {
        const Cls = class extends Base {
            constructor(...args) {
                super(...args);
                this.setupClass();
            }
        };
        ObjectUtils.mixin(Cls, [BaseClass]);
        return Cls;
    }
}
BaseClass.Meta = MetaSymbol;

class Quat {
    constructor(x = 0, y = 0, z = 0, w = 1) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.w = w;
    }
    toString() {
        return `Quat<${this.x},${this.y},${this.z},${this.w}>`;
    }
    set(x, y, z, w) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.w = w;
        return this;
    }
    reset() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
        this.w = 1;
        return this;
    }
    copy(dest) {
        if (dest === undefined) {
            dest = new Quat(this.x, this.y, this.z, this.w);
        }
        else {
            dest.x = this.x;
            dest.y = this.y;
            dest.z = this.z;
            dest.w = this.w;
        }
        return dest;
    }
    roll() {
        let x = this.x, y = this.y, z = this.z, w = this.w;
        return Math.atan2(2.0 * (x * y + w * z), w * w + x * x - y * y - z * z);
    }
    pitch() {
        let x = this.x, y = this.y, z = this.z, w = this.w;
        return Math.atan2(2.0 * (y * z + w * x), w * w - x * x - y * y + z * z);
    }
    yaw() {
        return Math.asin(2.0 * (this.x * this.z - this.w * this.y));
    }
    equals(other, threshold = 0.000001) {
        if (Math.abs(this.x - other.x) > threshold) {
            return false;
        }
        if (Math.abs(this.y - other.y) > threshold) {
            return false;
        }
        if (Math.abs(this.z - other.z) > threshold) {
            return false;
        }
        if (Math.abs(this.w - other.w) > threshold) {
            return false;
        }
        return true;
    }
    calculateW() {
        let x = this.x, y = this.y, z = this.z;
        this.w = -(Math.sqrt(Math.abs(1.0 - x * x - y * y - z * z)));
        return this;
    }
    static dot(q1, q2) {
        return q1.x * q2.x + q1.y * q2.y + q1.z * q2.z + q1.w * q2.w;
    }
    inverse() {
        let dot = Quat.dot(this, this);
        if (!dot) {
            this.reset();
            return this;
        }
        let invDot = dot ? 1.0 / dot : 0;
        this.x *= -invDot;
        this.y *= -invDot;
        this.z *= -invDot;
        this.w *= invDot;
        return this;
    }
    conjugate() {
        this.x *= -1;
        this.y *= -1;
        this.z *= -1;
        return this;
    }
    length() {
        let x = this.x, y = this.y, z = this.z, w = this.w;
        return Math.sqrt(x * x + y * y + z * z + w * w);
    }
    normalize(dest) {
        if (!dest) {
            dest = this;
        }
        let x = this.x, y = this.y, z = this.z, w = this.w;
        let length = Math.sqrt(x * x + y * y + z * z + w * w);
        if (!length) {
            dest.x = 0;
            dest.y = 0;
            dest.z = 0;
            dest.w = 0;
            return dest;
        }
        length = 1 / length;
        dest.x = x * length;
        dest.y = y * length;
        dest.z = z * length;
        dest.w = w * length;
        return dest;
    }
    add(other) {
        this.x += other.x;
        this.y += other.y;
        this.z += other.z;
        this.w += other.w;
        return this;
    }
    multiply(other) {
        let q1x = this.x, q1y = this.y, q1z = this.z, q1w = this.w;
        let q2x = other.x, q2y = other.y, q2z = other.z, q2w = other.w;
        this.x = q1x * q2w + q1w * q2x + q1y * q2z - q1z * q2y;
        this.y = q1y * q2w + q1w * q2y + q1z * q2x - q1x * q2z;
        this.z = q1z * q2w + q1w * q2z + q1x * q2y - q1y * q2x;
        this.w = q1w * q2w - q1x * q2x - q1y * q2y - q1z * q2z;
        return this;
    }
    multiplyVec3(vector, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        let x = vector.x, y = vector.y, z = vector.z;
        let qx = this.x, qy = this.y, qz = this.z, qw = this.w;
        let ix = qw * x + qy * z - qz * y, iy = qw * y + qz * x - qx * z, iz = qw * z + qx * y - qy * x, iw = -qx * x - qy * y - qz * z;
        dest.x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
        dest.y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
        dest.z = iz * qw + iw * -qz + ix * -qy - iy * -qx;
        return dest;
    }
    toMat4(dest) {
        if (!dest) {
            dest = new Mat4();
        }
        let x = this.x, y = this.y, z = this.z, w = this.w, x2 = x + x, y2 = y + y, z2 = z + z, xx = x * x2, xy = x * y2, xz = x * z2, yy = y * y2, yz = y * z2, zz = z * z2, wx = w * x2, wy = w * y2, wz = w * z2;
        dest.set([
            1 - (yy + zz), xy + wz, xz - wy, 0,
            xy - wz, 1 - (xx + zz), yz + wx, 0,
            xz + wy, yz - wx, 1 - (xx + yy), 0,
            0, 0, 0, 1
        ]);
        return dest;
    }
    static sum(q1, q2, dest) {
        if (!dest) {
            dest = new Quat();
        }
        dest.x = q1.x + q2.x;
        dest.y = q1.y + q2.y;
        dest.z = q1.z + q2.z;
        dest.w = q1.w + q2.w;
        return dest;
    }
    static product(q1, q2, dest) {
        if (!dest) {
            dest = new Quat();
        }
        let q1x = q1.x;
        let q1y = q1.y;
        let q1z = q1.z;
        let q1w = q1.w;
        let q2x = q2.x;
        let q2y = q2.y;
        let q2z = q2.z;
        let q2w = q2.w;
        dest.x = q1x * q2w + q1w * q2x + q1y * q2z - q1z * q2y;
        dest.y = q1y * q2w + q1w * q2y + q1z * q2x - q1x * q2z;
        dest.z = q1z * q2w + q1w * q2z + q1x * q2y - q1y * q2x;
        dest.w = q1w * q2w - q1x * q2x - q1y * q2y - q1z * q2z;
        return dest;
    }
    static cross(q1, q2, dest) {
        if (!dest) {
            dest = new Quat();
        }
        let q1x = q1.x;
        let q1y = q1.y;
        let q1z = q1.z;
        let q1w = q1.w;
        let q2x = q2.x;
        let q2y = q2.y;
        let q2z = q2.z;
        let q2w = q2.w;
        dest.x = q1w * q2z + q1z * q2w + q1x * q2y - q1y * q2x;
        dest.y = q1w * q2w - q1x * q2x - q1y * q2y - q1z * q2z;
        dest.z = q1w * q2x + q1x * q2w + q1y * q2z - q1z * q2y;
        dest.w = q1w * q2y + q1y * q2w + q1z * q2x - q1x * q2z;
        return dest;
    }
    static shortMix(q1, q2, time, dest) {
        if (!dest) {
            dest = new Quat();
        }
        if (time <= 0.0) {
            q1.copy(dest);
            return dest;
        }
        else if (time >= 1.0) {
            q2.copy(dest);
            return dest;
        }
        let cos = Quat.dot(q1, q2), q2a = q2.copy();
        if (cos < 0.0) {
            q2a.inverse();
            cos = -cos;
        }
        let k0, k1;
        if (cos > 0.9999) {
            k0 = 1 - time;
            k1 = 0 + time;
        }
        else {
            let sin = Math.sqrt(1 - cos * cos);
            let angle = Math.atan2(sin, cos);
            let oneOverSin = 1 / sin;
            k0 = Math.sin((1 - time) * angle) * oneOverSin;
            k1 = Math.sin((0 + time) * angle) * oneOverSin;
        }
        dest.x = k0 * q1.x + k1 * q2a.x;
        dest.y = k0 * q1.y + k1 * q2a.y;
        dest.z = k0 * q1.z + k1 * q2a.z;
        dest.w = k0 * q1.w + k1 * q2a.w;
        return dest;
    }
    static mix(q1, q2, time, dest) {
        if (!dest) {
            dest = new Quat();
        }
        let cosHalfTheta = q1.x * q2.x + q1.y * q2.y + q1.z * q2.z + q1.w * q2.w;
        if (Math.abs(cosHalfTheta) >= 1.0) {
            q1.copy(dest);
            return dest;
        }
        let halfTheta = Math.acos(cosHalfTheta), sinHalfTheta = Math.sqrt(1.0 - cosHalfTheta * cosHalfTheta);
        if (Math.abs(sinHalfTheta) < 0.001) {
            dest.x = q1.x * 0.5 + q2.x * 0.5;
            dest.y = q1.y * 0.5 + q2.y * 0.5;
            dest.z = q1.z * 0.5 + q2.z * 0.5;
            dest.w = q1.w * 0.5 + q2.w * 0.5;
            return dest;
        }
        let ratioA = Math.sin((1 - time) * halfTheta) / sinHalfTheta, ratioB = Math.sin(time * halfTheta) / sinHalfTheta;
        dest.x = q1.x * ratioA + q2.x * ratioB;
        dest.y = q1.y * ratioA + q2.y * ratioB;
        dest.z = q1.z * ratioA + q2.z * ratioB;
        dest.w = q1.w * ratioA + q2.w * ratioB;
        return dest;
    }
    /** Get quaternion from the supplied axis and angle (in degrees). */
    static fromAxis(axis, angle, dest) {
        if (!dest) {
            dest = new Quat();
        }
        angle *= 0.5;
        let sin = Math.sin(angle * Quat.degToRad);
        dest.x = axis.x * sin;
        dest.y = axis.y * sin;
        dest.z = axis.z * sin;
        dest.w = Math.cos(angle * Quat.degToRad);
        return dest;
    }
    /** Get quaternion from the supplied euler angles (in degrees) with rotation order XYZ. */
    static fromRotationXYZ(angleX, angleY, angleZ, dest) {
        if (!dest) {
            dest = new Quat();
        }
        angleX = angleX * Quat.degToRad;
        angleY = angleY * Quat.degToRad;
        angleZ = angleZ * Quat.degToRad;
        let sx = Math.sin(angleX * 0.5);
        let cx = Math.cos(angleX * 0.5);
        let sy = Math.sin(angleY * 0.5);
        let cy = Math.cos(angleY * 0.5);
        let sz = Math.sin(angleZ * 0.5);
        let cz = Math.cos(angleZ * 0.5);
        let cycz = cy * cz;
        let sysz = sy * sz;
        let sycz = sy * cz;
        let cysz = cy * sz;
        dest.w = (cx * cycz) - (sx * sysz);
        dest.x = (sx * cycz) + (cx * sysz);
        dest.y = (cx * sycz) - (sx * cysz);
        dest.z = (cx * cysz) + (sx * sycz);
        return dest;
    }
    /** Get quaternion from the supplied euler angles (in degrees) with rotation order ZYX. */
    static fromRotationZYX(angleZ, angleY, angleX, dest) {
        if (!dest) {
            dest = new Quat();
        }
        angleX = angleX * Quat.degToRad;
        angleY = angleY * Quat.degToRad;
        angleZ = angleZ * Quat.degToRad;
        let sx = Math.sin(angleX * 0.5);
        let cx = Math.cos(angleX * 0.5);
        let sy = Math.sin(angleY * 0.5);
        let cy = Math.cos(angleY * 0.5);
        let sz = Math.sin(angleZ * 0.5);
        let cz = Math.cos(angleZ * 0.5);
        let cycz = cy * cz;
        let sysz = sy * sz;
        let sycz = sy * cz;
        let cysz = cy * sz;
        dest.w = (cx * cycz) + (sx * sysz);
        dest.x = (sx * cycz) - (cx * sysz);
        dest.y = (cx * sycz) + (sx * cysz);
        dest.z = (cx * cysz) - (sx * sycz);
        return dest;
    }
    /** Get quaternion from the supplied euler angles (in degrees) with rotation order YXZ. */
    static fromRotationYXZ(angleY, angleX, angleZ, dest) {
        if (!dest) {
            dest = new Quat();
        }
        angleX = angleX * Quat.degToRad;
        angleY = angleY * Quat.degToRad;
        angleZ = angleZ * Quat.degToRad;
        let sx = Math.sin(angleX * 0.5);
        let cx = Math.cos(angleX * 0.5);
        let sy = Math.sin(angleY * 0.5);
        let cy = Math.cos(angleY * 0.5);
        let sz = Math.sin(angleZ * 0.5);
        let cz = Math.cos(angleZ * 0.5);
        let x = cy * sx;
        let y = sy * cx;
        let z = sy * sx;
        let w = cy * cx;
        dest.x = (x * cz) + (y * sz);
        dest.y = (y * cz) - (x * sz);
        dest.z = (w * sz) - (z * cz);
        dest.w = (w * cz) + (z * sz);
        return dest;
    }
}
Quat.degToRad = Math.PI / 180;
Quat.radToDeg = 180 / Math.PI;

class Vec3 {
    constructor(x = 0, y = 0, z = 0) {
        this.x = x;
        this.y = y;
        this.z = z;
    }
    static get zero() { return new Vec3(0, 0, 0); }
    static get up() { return new Vec3(0, 1, 0); }
    static get right() { return new Vec3(1, 0, 0); }
    static get forward() { return new Vec3(0, 0, 1); }
    toString() {
        return `Vec3<${this.x.toFixed(5)},${this.y.toFixed(5)},${this.z.toFixed(5)}>`;
    }
    set(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    }
    toXYZ() {
        return { x: this.x, y: this.y, z: this.z };
    }
    reset() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
        return this;
    }
    copy(dest) {
        if (!dest) {
            return new Vec3(this.x, this.y, this.z);
        }
        else {
            dest.x = this.x;
            dest.y = this.y;
            dest.z = this.z;
            return dest;
        }
    }
    negate(dest) {
        if (!dest) {
            dest = this;
        }
        dest.x = -this.x;
        dest.y = -this.y;
        dest.z = -this.z;
        return dest;
    }
    equals(vector, threshold = 0.000001) {
        if (Math.abs(this.x - vector.x) > threshold) {
            return false;
        }
        if (Math.abs(this.y - vector.y) > threshold) {
            return false;
        }
        if (Math.abs(this.z - vector.z) > threshold) {
            return false;
        }
        return true;
    }
    length() {
        return Math.sqrt(this.squaredLength());
    }
    squaredLength() {
        const x = this.x, y = this.y, z = this.z;
        return (x * x + y * y + z * z);
    }
    add(vector) {
        this.x += vector.x;
        this.y += vector.y;
        this.z += vector.z;
        return this;
    }
    addScalar(value) {
        this.x += value;
        this.y += value;
        this.z += value;
        return this;
    }
    addXYZ(x, y, z) {
        this.x += x;
        this.y += y;
        this.z += z;
        return this;
    }
    subtract(vector) {
        this.x -= vector.x;
        this.y -= vector.y;
        this.z -= vector.z;
        return this;
    }
    subtractScalar(value) {
        this.x -= value;
        this.y -= value;
        this.z -= value;
        return this;
    }
    subtractXYZ(x, y, z) {
        this.x -= x;
        this.y -= y;
        this.z -= z;
        return this;
    }
    multiply(vector) {
        this.x *= vector.x;
        this.y *= vector.y;
        this.z *= vector.z;
        return this;
    }
    multiplyScalar(value) {
        this.x *= value;
        this.y *= value;
        this.z *= value;
        return this;
    }
    multiplyXYZ(x, y, z) {
        this.x *= x;
        this.y *= y;
        this.z *= z;
        return this;
    }
    divide(vector) {
        this.x /= vector.x;
        this.y /= vector.y;
        this.z /= vector.z;
        return this;
    }
    divideScalar(value) {
        this.x /= value;
        this.y /= value;
        this.z /= value;
        return this;
    }
    divideXYZ(x, y, z) {
        this.x /= x;
        this.y /= y;
        this.z /= z;
        return this;
    }
    static normalize(source, dest) {
        let length = source.length();
        if (length === 1) {
            return source.copy(dest);
        }
        if (length === 0) {
            dest.x = 0;
            dest.y = 0;
            dest.z = 0;
            return dest;
        }
        length = 1.0 / length;
        dest.x *= length;
        dest.y *= length;
        dest.z *= length;
        return dest;
    }
    normalize() {
        let length = this.length();
        if (length === 1) {
            return this;
        }
        if (length === 0) {
            this.x = 0;
            this.y = 0;
            this.z = 0;
            return this;
        }
        length = 1.0 / length;
        this.x *= length;
        this.y *= length;
        this.z *= length;
        return this;
    }
    multiplyByQuat(quat, dest) {
        if (!dest) {
            dest = this;
        }
        return quat.multiplyVec3(this, dest);
    }
    static cross(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        const x = vector.x;
        const y = vector.y;
        const z = vector.z;
        const x2 = vector2.x;
        const y2 = vector2.y;
        const z2 = vector2.z;
        dest.x = y * z2 - z * y2;
        dest.y = z * x2 - x * z2;
        dest.z = x * y2 - y * x2;
        return dest;
    }
    static dot(vector, vector2) {
        const x = vector.x;
        const y = vector.y;
        const z = vector.z;
        const x2 = vector2.x;
        const y2 = vector2.y;
        const z2 = vector2.z;
        return (x * x2 + y * y2 + z * z2);
    }
    static distance(vector, vector2) {
        return Math.sqrt(this.squaredDistance(vector, vector2));
    }
    static squaredDistance(vector, vector2) {
        let x = vector2.x - vector.x;
        let y = vector2.y - vector.y;
        let z = vector2.z - vector.z;
        return (x * x + y * y + z * z);
    }
    static direction(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        const x = vector.x - vector2.x;
        const y = vector.y - vector2.y;
        const z = vector.z - vector2.z;
        let length = Math.sqrt(x * x + y * y + z * z);
        if (length === 0) {
            dest.x = 0;
            dest.y = 0;
            dest.z = 0;
            return dest;
        }
        length = 1 / length;
        dest.x = x * length;
        dest.y = y * length;
        dest.z = z * length;
        return dest;
    }
    static mix(vector, vector2, time, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        dest.x = vector.x + time * (vector2.x - vector.x);
        dest.y = vector.y + time * (vector2.y - vector.y);
        dest.z = vector.z + time * (vector2.z - vector.z);
        return dest;
    }
    static sum(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        dest.x = vector.x + vector2.x;
        dest.y = vector.y + vector2.y;
        dest.z = vector.z + vector2.z;
        return dest;
    }
    static difference(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        dest.x = vector.x - vector2.x;
        dest.y = vector.y - vector2.y;
        dest.z = vector.z - vector2.z;
        return dest;
    }
    static product(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        dest.x = vector.x * vector2.x;
        dest.y = vector.y * vector2.y;
        dest.z = vector.z * vector2.z;
        return dest;
    }
    static quotient(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        dest.x = vector.x / vector2.x;
        dest.y = vector.y / vector2.y;
        dest.z = vector.z / vector2.z;
        return dest;
    }
    toQuat(dest) {
        if (!dest) {
            dest = new Quat();
        }
        let cx = Math.cos(this.x * 0.5);
        let sx = Math.sin(this.x * 0.5);
        let cy = Math.cos(this.y * 0.5);
        let sy = Math.sin(this.y * 0.5);
        let cz = Math.cos(this.z * 0.5);
        let sz = Math.sin(this.z * 0.5);
        dest.x = sx * cy * cz - cx * sy * sz;
        dest.y = cx * sy * cz + sx * cy * sz;
        dest.z = cx * cy * sz - sx * sy * cz;
        dest.w = cx * cy * cz + sx * sy * sz;
        return dest;
    }
    static unproject(vec, view, proj, viewport) {
        // NOTE not tested
        // build and invert viewproj matrix
        let matrix = proj.copy().multiply(view);
        let invMat = matrix.inverse();
        if (invMat === null) {
            return null;
        }
        // apply viewport transform
        let x = (vec.x - viewport[0]) * 2.0 / viewport[2] - 1.0;
        let y = (vec.y - viewport[1]) * 2.0 / viewport[3] - 1.0;
        let z = (vec.z * 2.0) - 1.0;
        let w = 1.0;
        let tx = invMat.at(0) * x + invMat.at(4) * y + invMat.at(8) * z + invMat.at(12) * w;
        let ty = invMat.at(1) * x + invMat.at(5) * y + invMat.at(9) * z + invMat.at(13) * w;
        let tz = invMat.at(2) * x + invMat.at(6) * y + invMat.at(10) * z + invMat.at(14) * w;
        let tw = invMat.at(3) * x + invMat.at(7) * y + invMat.at(11) * z + invMat.at(15) * w;
        if (tw === 0.0) {
            return null;
        }
        return new Vec3(tx / tw, ty / tw, tz / tw);
    }
}

class Mat4 {
    constructor(values) {
        if (values !== undefined) {
            if (values instanceof Float32Array) {
                this.values = values;
            }
            else {
                this.values = new Float32Array(values);
            }
        }
        else {
            this.values = new Float32Array(Mat4.IdentityArray.buffer.slice(0));
        }
        this._buffer = this.values.buffer;
    }
    get raw() {
        return this.values;
    }
    get buffer() {
        return this._buffer;
    }
    at(index) {
        return this.values[index];
    }
    set(values) {
        for (let i = 0; i < 16; i++) {
            this.values[i] = values[i];
        }
        return this;
    }
    reset() {
        this.values.set(Mat4.IdentityArray, 0);
        return this;
    }
    copy(dest) {
        if (!dest) {
            dest = new Mat4();
        }
        for (let i = 0; i < 16; i++) {
            dest.values[i] = this.values[i];
        }
        return dest;
    }
    all() {
        let data = [];
        for (let i = 0; i < 16; i++) {
            data[i] = this.values[i];
        }
        return data;
    }
    row(index) {
        return [
            this.values[index * 4 + 0],
            this.values[index * 4 + 1],
            this.values[index * 4 + 2],
            this.values[index * 4 + 3]
        ];
    }
    col(index) {
        return [
            this.values[index],
            this.values[index + 4],
            this.values[index + 8],
            this.values[index + 12]
        ];
    }
    equals(matrix, threshold = 0.000001) {
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.values[i] - matrix.at(i)) > threshold) {
                return false;
            }
        }
        return true;
    }
    determinant() {
        let a00 = this.values[0], a01 = this.values[1], a02 = this.values[2], a03 = this.values[3], a10 = this.values[4], a11 = this.values[5], a12 = this.values[6], a13 = this.values[7], a20 = this.values[8], a21 = this.values[9], a22 = this.values[10], a23 = this.values[11], a30 = this.values[12], a31 = this.values[13], a32 = this.values[14], a33 = this.values[15];
        let det00 = a00 * a11 - a01 * a10, det01 = a00 * a12 - a02 * a10, det02 = a00 * a13 - a03 * a10, det03 = a01 * a12 - a02 * a11, det04 = a01 * a13 - a03 * a11, det05 = a02 * a13 - a03 * a12, det06 = a20 * a31 - a21 * a30, det07 = a20 * a32 - a22 * a30, det08 = a20 * a33 - a23 * a30, det09 = a21 * a32 - a22 * a31, det10 = a21 * a33 - a23 * a31, det11 = a22 * a33 - a23 * a32;
        return (det00 * det11 - det01 * det10 + det02 * det09 + det03 * det08 - det04 * det07 + det05 * det06);
    }
    transpose() {
        let temp01 = this.values[1], temp02 = this.values[2], temp03 = this.values[3], temp12 = this.values[6], temp13 = this.values[7], temp23 = this.values[11];
        this.values[1] = this.values[4];
        this.values[2] = this.values[8];
        this.values[3] = this.values[12];
        this.values[4] = temp01;
        this.values[6] = this.values[9];
        this.values[7] = this.values[13];
        this.values[8] = temp02;
        this.values[9] = temp12;
        this.values[11] = this.values[14];
        this.values[12] = temp03;
        this.values[13] = temp13;
        this.values[14] = temp23;
        return this;
    }
    inverse() {
        let a00 = this.values[0], a01 = this.values[1], a02 = this.values[2], a03 = this.values[3], a10 = this.values[4], a11 = this.values[5], a12 = this.values[6], a13 = this.values[7], a20 = this.values[8], a21 = this.values[9], a22 = this.values[10], a23 = this.values[11], a30 = this.values[12], a31 = this.values[13], a32 = this.values[14], a33 = this.values[15];
        let det00 = a00 * a11 - a01 * a10, det01 = a00 * a12 - a02 * a10, det02 = a00 * a13 - a03 * a10, det03 = a01 * a12 - a02 * a11, det04 = a01 * a13 - a03 * a11, det05 = a02 * a13 - a03 * a12, det06 = a20 * a31 - a21 * a30, det07 = a20 * a32 - a22 * a30, det08 = a20 * a33 - a23 * a30, det09 = a21 * a32 - a22 * a31, det10 = a21 * a33 - a23 * a31, det11 = a22 * a33 - a23 * a32;
        let det = (det00 * det11 - det01 * det10 + det02 * det09 + det03 * det08 - det04 * det07 + det05 * det06);
        if (!det) {
            return null;
        }
        det = 1.0 / det;
        this.values[0] = (a11 * det11 - a12 * det10 + a13 * det09) * det;
        this.values[1] = (-a01 * det11 + a02 * det10 - a03 * det09) * det;
        this.values[2] = (a31 * det05 - a32 * det04 + a33 * det03) * det;
        this.values[3] = (-a21 * det05 + a22 * det04 - a23 * det03) * det;
        this.values[4] = (-a10 * det11 + a12 * det08 - a13 * det07) * det;
        this.values[5] = (a00 * det11 - a02 * det08 + a03 * det07) * det;
        this.values[6] = (-a30 * det05 + a32 * det02 - a33 * det01) * det;
        this.values[7] = (a20 * det05 - a22 * det02 + a23 * det01) * det;
        this.values[8] = (a10 * det10 - a11 * det08 + a13 * det06) * det;
        this.values[9] = (-a00 * det10 + a01 * det08 - a03 * det06) * det;
        this.values[10] = (a30 * det04 - a31 * det02 + a33 * det00) * det;
        this.values[11] = (-a20 * det04 + a21 * det02 - a23 * det00) * det;
        this.values[12] = (-a10 * det09 + a11 * det07 - a12 * det06) * det;
        this.values[13] = (a00 * det09 - a01 * det07 + a02 * det06) * det;
        this.values[14] = (-a30 * det03 + a31 * det01 - a32 * det00) * det;
        this.values[15] = (a20 * det03 - a21 * det01 + a22 * det00) * det;
        return this;
    }
    multiply(matrix) {
        let a00 = this.values[0], a01 = this.values[1], a02 = this.values[2], a03 = this.values[3];
        let a10 = this.values[4], a11 = this.values[5], a12 = this.values[6], a13 = this.values[7];
        let a20 = this.values[8], a21 = this.values[9], a22 = this.values[10], a23 = this.values[11];
        let a30 = this.values[12], a31 = this.values[13], a32 = this.values[14], a33 = this.values[15];
        let b0 = matrix.at(0), b1 = matrix.at(1), b2 = matrix.at(2), b3 = matrix.at(3);
        this.values[0] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
        this.values[1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
        this.values[2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
        this.values[3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
        b0 = matrix.at(4);
        b1 = matrix.at(5);
        b2 = matrix.at(6);
        b3 = matrix.at(7);
        this.values[4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
        this.values[5] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
        this.values[6] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
        this.values[7] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
        b0 = matrix.at(8);
        b1 = matrix.at(9);
        b2 = matrix.at(10);
        b3 = matrix.at(11);
        this.values[8] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
        this.values[9] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
        this.values[10] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
        this.values[11] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
        b0 = matrix.at(12);
        b1 = matrix.at(13);
        b2 = matrix.at(14);
        b3 = matrix.at(15);
        this.values[12] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
        this.values[13] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
        this.values[14] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
        this.values[15] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
        return this;
    }
    multiplyVec3(vector) {
        let x = vector.x, y = vector.y, z = vector.z;
        return new Vec3(this.values[0] * x + this.values[4] * y + this.values[8] * z + this.values[12], this.values[1] * x + this.values[5] * y + this.values[9] * z + this.values[13], this.values[2] * x + this.values[6] * y + this.values[10] * z + this.values[14]);
    }
    translate(vector) {
        let x = vector.x;
        let y = vector.y;
        let z = vector.z;
        this.values[12] += this.values[0] * x + this.values[4] * y + this.values[8] * z;
        this.values[13] += this.values[1] * x + this.values[5] * y + this.values[9] * z;
        this.values[14] += this.values[2] * x + this.values[6] * y + this.values[10] * z;
        this.values[15] += this.values[3] * x + this.values[7] * y + this.values[11] * z;
        return this;
    }
    scale(vector) {
        let x = vector.x;
        let y = vector.y;
        let z = vector.z;
        this.values[0] *= x;
        this.values[1] *= x;
        this.values[2] *= x;
        this.values[3] *= x;
        this.values[4] *= y;
        this.values[5] *= y;
        this.values[6] *= y;
        this.values[7] *= y;
        this.values[8] *= z;
        this.values[9] *= z;
        this.values[10] *= z;
        this.values[11] *= z;
        return this;
    }
    rotate(angle, axis) {
        let x = axis.x;
        let y = axis.y;
        let z = axis.z;
        let length = Math.sqrt(x * x + y * y + z * z);
        if (!length) {
            return null;
        }
        if (length !== 1) {
            length = 1 / length;
            x *= length;
            y *= length;
            z *= length;
        }
        let s = Math.sin(angle);
        let c = Math.cos(angle);
        let t = 1.0 - c;
        let a00 = this.values[0], a01 = this.values[1], a02 = this.values[2], a03 = this.values[3], a10 = this.values[4], a11 = this.values[5], a12 = this.values[6], a13 = this.values[7], a20 = this.values[8], a21 = this.values[9], a22 = this.values[10], a23 = this.values[11];
        let b00 = x * x * t + c, b01 = y * x * t + z * s, b02 = z * x * t - y * s, b10 = x * y * t - z * s, b11 = y * y * t + c, b12 = z * y * t + x * s, b20 = x * z * t + y * s, b21 = y * z * t - x * s, b22 = z * z * t + c;
        this.values[0] = a00 * b00 + a10 * b01 + a20 * b02;
        this.values[1] = a01 * b00 + a11 * b01 + a21 * b02;
        this.values[2] = a02 * b00 + a12 * b01 + a22 * b02;
        this.values[3] = a03 * b00 + a13 * b01 + a23 * b02;
        this.values[4] = a00 * b10 + a10 * b11 + a20 * b12;
        this.values[5] = a01 * b10 + a11 * b11 + a21 * b12;
        this.values[6] = a02 * b10 + a12 * b11 + a22 * b12;
        this.values[7] = a03 * b10 + a13 * b11 + a23 * b12;
        this.values[8] = a00 * b20 + a10 * b21 + a20 * b22;
        this.values[9] = a01 * b20 + a11 * b21 + a21 * b22;
        this.values[10] = a02 * b20 + a12 * b21 + a22 * b22;
        this.values[11] = a03 * b20 + a13 * b21 + a23 * b22;
        return this;
    }
    static frustum(left, right, bottom, top, near, far) {
        let rl = (right - left), tb = (top - bottom), fn = (far - near);
        return new Mat4([
            (near * 2) / rl, 0, 0, 0,
            0, (near * 2) / tb, 0, 0,
            (right + left) / rl, (top + bottom) / tb, -(far + near) / fn, -1,
            0, 0, -(far * near * 2) / fn, 0
        ]);
    }
    static perspective(fov, aspect, near, far) {
        let top = near * Math.tan(fov * Math.PI / 360.0), right = top * aspect;
        return Mat4.frustum(-right, right, -top, top, near, far);
    }
    static orthographic(left, right, bottom, top, near, far) {
        let rl = (right - left), tb = (top - bottom), fn = (far - near);
        return new Mat4([
            2 / rl, 0, 0, 0,
            0, 2 / tb, 0, 0,
            0, 0, -2 / fn, 0,
            -(left + right) / rl, -(top + bottom) / tb, -(far + near) / fn, 1
        ]);
    }
    static lookAt(position, target, up = Vec3.up) {
        if (position.equals(target)) {
            return new Mat4();
        }
        let z = Vec3.difference(position, target).normalize();
        let x = Vec3.cross(up, z).normalize();
        let y = Vec3.cross(z, x).normalize();
        return new Mat4([
            x.x, y.x, z.x, 0,
            x.y, y.y, z.y, 0,
            x.z, y.z, z.z, 0,
            -Vec3.dot(x, position), -Vec3.dot(y, position), -Vec3.dot(z, position), 1
        ]);
    }
    static product(m1, m2, result) {
        let a00 = m1.at(0), a01 = m1.at(1), a02 = m1.at(2), a03 = m1.at(3), a10 = m1.at(4), a11 = m1.at(5), a12 = m1.at(6), a13 = m1.at(7), a20 = m1.at(8), a21 = m1.at(9), a22 = m1.at(10), a23 = m1.at(11), a30 = m1.at(12), a31 = m1.at(13), a32 = m1.at(14), a33 = m1.at(15);
        let b00 = m2.at(0), b01 = m2.at(1), b02 = m2.at(2), b03 = m2.at(3), b10 = m2.at(4), b11 = m2.at(5), b12 = m2.at(6), b13 = m2.at(7), b20 = m2.at(8), b21 = m2.at(9), b22 = m2.at(10), b23 = m2.at(11), b30 = m2.at(12), b31 = m2.at(13), b32 = m2.at(14), b33 = m2.at(15);
        if (result) {
            result.set([
                b00 * a00 + b01 * a10 + b02 * a20 + b03 * a30,
                b00 * a01 + b01 * a11 + b02 * a21 + b03 * a31,
                b00 * a02 + b01 * a12 + b02 * a22 + b03 * a32,
                b00 * a03 + b01 * a13 + b02 * a23 + b03 * a33,
                b10 * a00 + b11 * a10 + b12 * a20 + b13 * a30,
                b10 * a01 + b11 * a11 + b12 * a21 + b13 * a31,
                b10 * a02 + b11 * a12 + b12 * a22 + b13 * a32,
                b10 * a03 + b11 * a13 + b12 * a23 + b13 * a33,
                b20 * a00 + b21 * a10 + b22 * a20 + b23 * a30,
                b20 * a01 + b21 * a11 + b22 * a21 + b23 * a31,
                b20 * a02 + b21 * a12 + b22 * a22 + b23 * a32,
                b20 * a03 + b21 * a13 + b22 * a23 + b23 * a33,
                b30 * a00 + b31 * a10 + b32 * a20 + b33 * a30,
                b30 * a01 + b31 * a11 + b32 * a21 + b33 * a31,
                b30 * a02 + b31 * a12 + b32 * a22 + b33 * a32,
                b30 * a03 + b31 * a13 + b32 * a23 + b33 * a33
            ]);
            return result;
        }
        else {
            return new Mat4([
                b00 * a00 + b01 * a10 + b02 * a20 + b03 * a30,
                b00 * a01 + b01 * a11 + b02 * a21 + b03 * a31,
                b00 * a02 + b01 * a12 + b02 * a22 + b03 * a32,
                b00 * a03 + b01 * a13 + b02 * a23 + b03 * a33,
                b10 * a00 + b11 * a10 + b12 * a20 + b13 * a30,
                b10 * a01 + b11 * a11 + b12 * a21 + b13 * a31,
                b10 * a02 + b11 * a12 + b12 * a22 + b13 * a32,
                b10 * a03 + b11 * a13 + b12 * a23 + b13 * a33,
                b20 * a00 + b21 * a10 + b22 * a20 + b23 * a30,
                b20 * a01 + b21 * a11 + b22 * a21 + b23 * a31,
                b20 * a02 + b21 * a12 + b22 * a22 + b23 * a32,
                b20 * a03 + b21 * a13 + b22 * a23 + b23 * a33,
                b30 * a00 + b31 * a10 + b32 * a20 + b33 * a30,
                b30 * a01 + b31 * a11 + b32 * a21 + b33 * a31,
                b30 * a02 + b31 * a12 + b32 * a22 + b33 * a32,
                b30 * a03 + b31 * a13 + b32 * a23 + b33 * a33
            ]);
        }
    }
}
Mat4.IdentityArray = new Float32Array([
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1
]);

class Vec2 {
    constructor(x = 0, y = 0) {
        this.x = x;
        this.y = y;
    }
    static get zero() { return new Vec2(0, 0); }
    toString() {
        return `Vec2<${this.x.toFixed(5)},${this.y.toFixed(5)}>`;
    }
    set(x, y) {
        this.x = x;
        this.y = y;
        return this;
    }
    toXY() {
        return { x: this.x, y: this.y };
    }
    toUV(precision) {
        if (precision !== undefined) {
            return { u: parseFloat(this.x.toFixed(precision)), v: parseFloat(this.y.toFixed(precision)) };
        }
        else {
            return { u: this.x, v: this.y };
        }
    }
    reset() {
        this.x = 0;
        this.y = 0;
        return this;
    }
    copy(dest) {
        if (!dest) {
            return new Vec2(this.x, this.y);
        }
        else {
            dest.x = this.x;
            dest.y = this.y;
            return dest;
        }
    }
    negate(dest) {
        if (!dest) {
            dest = this;
        }
        dest.x = -this.x;
        dest.y = -this.y;
        return dest;
    }
    equals(vector, threshold = 0.000001) {
        if (Math.abs(this.x - vector.x) > threshold) {
            return false;
        }
        if (Math.abs(this.y - vector.y) > threshold) {
            return false;
        }
        return true;
    }
    length() {
        return Math.sqrt(this.squaredLength());
    }
    squaredLength() {
        let { x, y } = this;
        return (x * x + y * y);
    }
    add(vector) {
        this.x += vector.x;
        this.y += vector.y;
        return this;
    }
    addScalar(value) {
        this.x += value;
        this.y += value;
        return this;
    }
    addXY(x, y) {
        this.x += x;
        this.y += y;
        return this;
    }
    subtract(vector) {
        this.x -= vector.x;
        this.y -= vector.y;
        return this;
    }
    subtractScalar(value) {
        this.x -= value;
        this.y -= value;
        return this;
    }
    subtractXY(x, y) {
        this.x -= x;
        this.y -= y;
        return this;
    }
    multiply(vector) {
        this.x *= vector.x;
        this.y *= vector.y;
        return this;
    }
    multiplyScalar(value) {
        this.x *= value;
        this.y *= value;
        return this;
    }
    multiplyXY(x, y) {
        this.x *= x;
        this.y *= y;
        return this;
    }
    divide(vector) {
        this.x /= vector.x;
        this.y /= vector.y;
        return this;
    }
    divideScalar(value) {
        this.x /= value;
        this.y /= value;
        return this;
    }
    divideXY(x, y) {
        this.x /= x;
        this.y /= y;
        return this;
    }
    scale(value, dest) {
        if (!dest) {
            dest = this;
        }
        dest.x *= value;
        dest.y *= value;
        return dest;
    }
    normalize(dest) {
        if (!dest) {
            dest = this;
        }
        let length = this.length();
        if (length === 1) {
            return this;
        }
        if (length === 0) {
            dest.x = 0;
            dest.y = 0;
            return dest;
        }
        length = 1.0 / length;
        dest.x *= length;
        dest.y *= length;
        return dest;
    }
    static cross(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec3();
        }
        dest.x = 0;
        dest.y = 0;
        dest.z = vector.x * vector2.y - vector.y * vector2.x;
        return dest;
    }
    static dot(vector, vector2) {
        return (vector.x * vector2.x + vector.y * vector2.y);
    }
    static distance(vector, vector2) {
        return Math.sqrt(this.squaredDistance(vector, vector2));
    }
    static squaredDistance(vector, vector2) {
        let x = vector2.x - vector.x;
        let y = vector2.y - vector.y;
        return (x * x + y * y);
    }
    static direction(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec2();
        }
        let x = vector.x - vector2.x;
        let y = vector.y - vector2.y;
        let length = Math.sqrt(x * x + y * y);
        if (length === 0) {
            dest.x = 0;
            dest.y = 0;
            return dest;
        }
        length = 1 / length;
        dest.x = x * length;
        dest.y = y * length;
        return dest;
    }
    static mix(vector, vector2, time, dest) {
        if (!dest) {
            dest = new Vec2();
        }
        let x = vector.x;
        let y = vector.y;
        let x2 = vector2.x;
        let y2 = vector2.y;
        dest.x = x + time * (x2 - x);
        dest.y = y + time * (y2 - y);
        return dest;
    }
    static sum(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec2();
        }
        dest.x = vector.x + vector2.x;
        dest.y = vector.y + vector2.y;
        return dest;
    }
    static difference(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec2();
        }
        dest.x = vector.x - vector2.x;
        dest.y = vector.y - vector2.y;
        return dest;
    }
    static product(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec2();
        }
        dest.x = vector.x * vector2.x;
        dest.y = vector.y * vector2.y;
        return dest;
    }
    static quotient(vector, vector2, dest) {
        if (!dest) {
            dest = new Vec2();
        }
        dest.x = vector.x / vector2.x;
        dest.y = vector.y / vector2.y;
        return dest;
    }
}

const internalBuffer = new ArrayBuffer(8);
const internalBytes = new Uint8Array(internalBuffer);
const internalFloatArray = new Float32Array(internalBuffer);
const internalDoubleArray = new Float64Array(internalBuffer);
var StringMode;
(function (StringMode) {
    StringMode[StringMode["Fixed8"] = 0] = "Fixed8";
    StringMode[StringMode["Fixed16"] = 1] = "Fixed16";
    StringMode[StringMode["Fixed24"] = 2] = "Fixed24";
    StringMode[StringMode["Fixed32"] = 3] = "Fixed32";
    StringMode[StringMode["Dynamic"] = 4] = "Dynamic";
})(StringMode || (StringMode = {}));
class InternalBuffer {
    constructor(size) {
        this._readPos = 0;
        this._writePos = 0;
        this._buffer = new ArrayBuffer(size);
        this.buffer = new Uint8Array(this._buffer);
        this.bufferS = new Int8Array(this._buffer);
        this._limit = this.buffer.byteLength;
    }
    toBuffer() {
        return this.buffer.slice(0, this._writePos);
    }
}
class AbstractBuffer {
    /* Utils */
    static isFloat32(value) {
        internalFloatArray[0] = +value;
        return value === internalFloatArray[0];
    }
    writeUByte(value) {
        this.writeUInt8(value);
    }
    readUByte() {
        return this.readUInt8();
    }
    writeByte(value) {
        this.writeInt8(value);
    }
    readByte() {
        return this.readInt8();
    }
    readString() {
        const length = this.readUInt32();
        let buf = this.readBuffer(length);
        return this.stringFromBuffer(buf);
    }
    writeString(string) {
        let buf = this.bufferFromString(string);
        this.writeUInt32(buf.byteLength);
        this.writeBuffer(buf);
    }
    readVarInt() {
        let value = 0;
        let digits = 0;
        while (value <= 0) {
            let next = this.readInt8();
            if (next < 0) {
                value = (next << digits) + value;
            }
            else {
                value = (next << digits) - value;
            }
            digits += 7;
        }
        return value;
    }
    writeVarInt(value) {
        while (value >= 128) {
            this.writeInt8(-(value & 0x7F));
            value >>>= 7;
        }
        this.writeInt8(value);
    }
    readStringMode(mode) {
        if (mode === StringMode.Fixed8) {
            const length = this.readUInt8();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Fixed16) {
            const length = this.readUInt16();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Fixed24) {
            const length = this.readUInt24();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Fixed32) {
            const length = this.readUInt32();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Dynamic) {
            let length = 0;
            let digits = 0;
            while (length <= 0) {
                let next = this.readInt8();
                if (next < 0) {
                    length = (next << digits) + length;
                }
                else {
                    length = (next << digits) - length;
                }
                digits += 7;
            }
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else {
            throw new Error("Invalid StringMode!");
        }
    }
    writeStringMode(mode, string) {
        if (mode === StringMode.Fixed8) {
            let buf = this.bufferFromString(string);
            this.writeUInt8(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Fixed16) {
            let buf = this.bufferFromString(string);
            this.writeUInt16(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Fixed24) {
            let buf = this.bufferFromString(string);
            this.writeUInt24(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Fixed32) {
            let buf = this.bufferFromString(string);
            this.writeUInt32(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Dynamic) {
            let buf = this.bufferFromString(string);
            let length = buf.byteLength;
            while (length >= 128) {
                this.writeInt8(-(length & 0x7F));
                length >>>= 7;
            }
            this.writeInt8(length);
            this.writeBuffer(buf);
        }
        else {
            throw new Error("Invalid StringMode!");
        }
    }
    bufferFromString(string) {
        // browser optimization
        if (global.TextEncoder !== undefined) {
            let encoder = new TextEncoder();
            return encoder.encode(string);
        }
        return BinaryUtils.stringToUint8Array(string);
    }
    stringFromBuffer(buffer) {
        // browser optimization
        if (global.TextDecoder !== undefined) {
            let decoder = new TextDecoder("utf-8");
            return decoder.decode(buffer);
        }
        return BinaryUtils.uint8ToStringArray(buffer);
    }
}

class BufferError extends Error {
    constructor(message) {
        super(message);
        this.error = message;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

class WrappedBuffer extends AbstractBuffer {
    constructor(buffer, offset, length) {
        super();
        this.offset = offset;
        if (!(buffer instanceof ArrayBuffer)) {
            throw new BufferError("Couldn't create WrappedBuffer, invalid constructor argument " + typeof (buffer) + ", expected ArrayBuffer!");
        }
        offset = offset | 0;
        length = length | 0;
        this._buffer = buffer;
        this.buffer = new Uint8Array(buffer, offset, length);
        this.bufferS = new Int8Array(buffer, offset, length);
        this._readPos = 0;
        this._writePos = 0;
        this._limit = this.buffer.byteLength;
    }
    getInternalBuffers() {
        return [this];
    }
    static fromBuffer(buffer) {
        if (!(buffer instanceof Uint8Array)) {
            throw new BufferError("Couldn't create WrappedBuffer(fromBuffer), invalid constructor argument " + typeof (buffer) + ", expected Uint8Array!");
        }
        return new WrappedBuffer(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    }
    static fromArrayBuffer(buffer) {
        if (!(buffer instanceof ArrayBuffer)) {
            throw new BufferError("Couldn't create WrappedBuffer(fromArrayBuffer), invalid constructor argument " + typeof (buffer) + ", expected ArrayBuffer!");
        }
        return new WrappedBuffer(buffer, 0, buffer.byteLength);
    }
    static allocate(size) {
        size = size | 0;
        return new WrappedBuffer(new ArrayBuffer(size), 0, size);
    }
    subBuffer(start, length) {
        return new WrappedBuffer(this._buffer, this.offset + start, length);
    }
    set writePos(value) {
        if (value > this._limit) {
            throw new BufferError("[WrappedBuffer::writePos] Can't set writePos above buffer's capacity!");
        }
        if (value < 0) {
            throw new BufferError("[WrappedBuffer::writePos] Can't set writePos below 0!");
        }
        this._writePos = value;
    }
    get writePos() {
        return this._writePos;
    }
    set readPos(value) {
        if (value > this._limit) {
            throw new BufferError("[WrappedBuffer::readPos] Can't set readPos above buffer's capacity!");
        }
        if (value < 0) {
            throw new BufferError("[WrappedBuffer::readPos] Can't set readPos below 0!");
        }
        this._readPos = value;
    }
    get readPos() {
        return this._readPos;
    }
    set limit(value) {
        if (value > this.buffer.byteLength) {
            throw new BufferError("[WrappedBuffer::limit] Can't set limit above buffer's capacity!");
        }
        if (value < 0) {
            throw new BufferError("[WrappedBuffer::limit] Can't set limit below 0!");
        }
        this._limit = value;
    }
    get limit() {
        return this._limit;
    }
    resetLimit() {
        this.limit = this.buffer.byteLength;
    }
    restart() {
        this._readPos = 0;
        this._writePos = 0;
    }
    writeBool(value) {
        if (this._limit < (this._writePos + 1)) {
            throw new BufferError("[WrappedBuffer::writeBool] buffer access out of bounds!");
        }
        this.buffer[this._writePos++] = (value ? 1 : -1);
    }
    readBool() {
        if (this._limit < (this._readPos + 1)) {
            throw new BufferError("[WrappedBuffer::readBool] buffer access out of bounds!");
        }
        return (this.buffer[this._readPos++] === 1);
    }
    writeUInt8(value) {
        if (this._limit < (this._writePos + 1)) {
            throw new BufferError("[WrappedBuffer::writeUByte] buffer access out of bounds!");
        }
        this.buffer[this._writePos++] = (value | 0);
    }
    readUInt8() {
        if (this._limit < (this._readPos + 1)) {
            throw new BufferError("[WrappedBuffer::readUByte] buffer access out of bounds!");
        }
        return this.buffer[this._readPos++];
    }
    writeInt8(value) {
        if (this._limit < (this._writePos + 1)) {
            throw new BufferError("[WrappedBuffer::writeByte] buffer access out of bounds!");
        }
        this.bufferS[this._writePos++] = (value | 0);
    }
    readInt8() {
        if (this._limit < (this._readPos + 1)) {
            throw new BufferError("[WrappedBuffer::readByte] buffer access out of bounds!");
        }
        return this.bufferS[this._readPos++];
    }
    writeInt16(value) {
        if (this._limit < (this._writePos + 2)) {
            throw new BufferError("[WrappedBuffer::writeInt16] buffer access out of bounds!");
        }
        value = value | 0;
        this.bufferS[this._writePos + 0] = (value >> 8);
        this.buffer[this._writePos + 1] = value;
        this._writePos += 2;
    }
    readInt16() {
        if (this._limit < (this._readPos + 2)) {
            throw new BufferError("[WrappedBuffer::readInt16] buffer access out of bounds!");
        }
        const out = (this.bufferS[this._readPos + 0] << 8) | this.buffer[this._readPos + 1];
        this._readPos += 2;
        return out;
    }
    writeUInt16(value) {
        if (this._limit < (this._writePos + 2)) {
            throw new BufferError("[WrappedBuffer::writeUInt16] buffer access out of bounds!");
        }
        value = value | 0;
        this.buffer[this._writePos + 0] = (value >> 8);
        this.buffer[this._writePos + 1] = value;
        this._writePos += 2;
    }
    readUInt16() {
        if (this._limit < (this._readPos + 2)) {
            throw new BufferError("[WrappedBuffer::readUInt16] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 0] << 8) | this.buffer[this._readPos + 1];
        this._readPos += 2;
        return out;
    }
    writeInt24(value) {
        if (this._limit < (this._writePos + 3)) {
            throw new BufferError("[WrappedBuffer::writeInt24] buffer access out of bounds!");
        }
        value = value | 0;
        this.bufferS[this._writePos + 0] = (value >> 16);
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 2] = value;
        this._writePos += 3;
    }
    readInt24() {
        if (this._limit < (this._readPos + 3)) {
            throw new BufferError("[WrappedBuffer::readInt24] buffer access out of bounds!");
        }
        const out = (this.bufferS[this._readPos + 0] << 16) | (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 2];
        this._readPos += 3;
        return out;
    }
    writeUInt24(value) {
        if (this._limit < (this._writePos + 3)) {
            throw new BufferError("[WrappedBuffer::writeUInt24] buffer access out of bounds!");
        }
        value = value >>> 0;
        this.buffer[this._writePos + 0] = (value >> 16);
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 2] = value;
        this._writePos += 3;
    }
    readUInt24() {
        if (this._limit < (this._readPos + 3)) {
            throw new BufferError("[WrappedBuffer::readUInt24] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 0] << 16) | (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 2];
        this._readPos += 3;
        return out >>> 0;
    }
    writeInt32(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[WrappedBuffer::writeInt32] buffer access out of bounds!");
        }
        value = value | 0;
        this.buffer[this._writePos + 0] = (value >> 24);
        this.buffer[this._writePos + 1] = (value >> 16);
        this.buffer[this._writePos + 2] = (value >> 8);
        this.buffer[this._writePos + 3] = value;
        this._writePos += 4;
    }
    readInt32() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[WrappedBuffer::readInt32] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 0] << 24) | (this.buffer[this._readPos + 1] << 16) | (this.buffer[this._readPos + 2] << 8) | this.buffer[this._readPos + 3];
        this._readPos += 4;
        return out;
    }
    writeUInt32(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[WrappedBuffer::writeInt32] buffer access out of bounds!");
        }
        value = value >>> 0;
        this.buffer[this._writePos + 0] = (value >> 24);
        this.buffer[this._writePos + 1] = (value >> 16);
        this.buffer[this._writePos + 2] = (value >> 8);
        this.buffer[this._writePos + 3] = value;
        this._writePos += 4;
    }
    readUInt32() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[WrappedBuffer::readInt32] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 0] << 24) | (this.buffer[this._readPos + 1] << 16) | (this.buffer[this._readPos + 2] << 8) | this.buffer[this._readPos + 3];
        this._readPos += 4;
        return out >>> 0;
    }
    writeFloat32(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[WrappedBuffer::writeFloat32] buffer access out of bounds!");
        }
        internalFloatArray[0] = +value;
        this.buffer[this._writePos + 0] = (internalBytes[0] | 0);
        this.buffer[this._writePos + 1] = (internalBytes[1] | 0);
        this.buffer[this._writePos + 2] = (internalBytes[2] | 0);
        this.buffer[this._writePos + 3] = (internalBytes[3] | 0);
        this._writePos += 4;
    }
    readFloat32() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[WrappedBuffer::readFloat32] buffer access out of bounds!");
        }
        internalBytes[0] = this.buffer[this._readPos + 0];
        internalBytes[1] = this.buffer[this._readPos + 1];
        internalBytes[2] = this.buffer[this._readPos + 2];
        internalBytes[3] = this.buffer[this._readPos + 3];
        this._readPos += 4;
        return internalFloatArray[0];
    }
    writeFloat64(value) {
        if (this._limit < (this._writePos + 8)) {
            throw new BufferError("[WrappedBuffer::writeFloat64] buffer access out of bounds!");
        }
        internalDoubleArray[0] = +value;
        this.buffer[this._writePos + 0] = (internalBytes[0] | 0);
        this.buffer[this._writePos + 1] = (internalBytes[1] | 0);
        this.buffer[this._writePos + 2] = (internalBytes[2] | 0);
        this.buffer[this._writePos + 3] = (internalBytes[3] | 0);
        this.buffer[this._writePos + 4] = (internalBytes[4] | 0);
        this.buffer[this._writePos + 5] = (internalBytes[5] | 0);
        this.buffer[this._writePos + 6] = (internalBytes[6] | 0);
        this.buffer[this._writePos + 7] = (internalBytes[7] | 0);
        this._writePos += 8;
    }
    readFloat64() {
        if (this._limit < (this._readPos + 8)) {
            throw new BufferError("[WrappedBuffer::readFloat64] buffer access out of bounds!");
        }
        internalBytes[0] = this.buffer[this._readPos + 0];
        internalBytes[1] = this.buffer[this._readPos + 1];
        internalBytes[2] = this.buffer[this._readPos + 2];
        internalBytes[3] = this.buffer[this._readPos + 3];
        internalBytes[4] = this.buffer[this._readPos + 4];
        internalBytes[5] = this.buffer[this._readPos + 5];
        internalBytes[6] = this.buffer[this._readPos + 6];
        internalBytes[7] = this.buffer[this._readPos + 7];
        this._readPos += 8;
        return internalDoubleArray[0];
    }
    setInt32(offset, value) {
        if (this._limit < (offset + 4)) {
            throw new BufferError("[WrappedBuffer::setInt32] buffer access out of bounds!");
        }
        offset = offset | 0;
        value = value | 0;
        this.buffer[offset] = (value >> 24);
        this.buffer[offset + 1] = (value >> 16);
        this.buffer[offset + 2] = (value >> 8);
        this.buffer[offset + 3] = value;
    }
    writeBuffer(buffer) {
        this.buffer.set(buffer, this._writePos);
        this._writePos += buffer.byteLength;
    }
    readBuffer(size) {
        let start = this._readPos;
        let buffer = this.buffer.slice(start, start + size);
        this._readPos += buffer.byteLength;
        return buffer;
    }
    toBuffer() {
        return this.buffer.slice(0, this.writePos);
    }
    /*****************************************************************
                        LE - little endian
    *****************************************************************/
    writeInt16LE(value) {
        if (this._limit < (this._writePos + 2)) {
            throw new BufferError("[WrappedBuffer::writeInt16] buffer access out of bounds!");
        }
        value = value | 0;
        this.bufferS[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 0] = value;
        this._writePos += 2;
    }
    readInt16LE() {
        if (this._limit < (this._readPos + 2)) {
            throw new BufferError("[WrappedBuffer::readInt16] buffer access out of bounds!");
        }
        const out = (this.bufferS[this._readPos + 1] << 8) | this.buffer[this._readPos + 0];
        this._readPos += 2;
        return out;
    }
    writeUInt16LE(value) {
        if (this._limit < (this._writePos + 2)) {
            throw new BufferError("[WrappedBuffer::writeUInt16] buffer access out of bounds!");
        }
        value = value | 0;
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 0] = value;
        this._writePos += 2;
    }
    readUInt16LE() {
        if (this._limit < (this._readPos + 2)) {
            throw new BufferError("[WrappedBuffer::readUInt16] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 0];
        this._readPos += 2;
        return out;
    }
    writeInt24LE(value) {
        if (this._limit < (this._writePos + 3)) {
            throw new BufferError("[WrappedBuffer::writeInt24] buffer access out of bounds!");
        }
        value = value | 0;
        this.bufferS[this._writePos + 2] = (value >> 16);
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 0] = value;
        this._writePos += 3;
    }
    readInt24LE() {
        if (this._limit < (this._readPos + 3)) {
            throw new BufferError("[WrappedBuffer::readInt24] buffer access out of bounds!");
        }
        const out = (this.bufferS[this._readPos + 2] << 16) | (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 0];
        this._readPos += 3;
        return out;
    }
    writeUInt24LE(value) {
        if (this._limit < (this._writePos + 3)) {
            throw new BufferError("[WrappedBuffer::writeUInt24] buffer access out of bounds!");
        }
        value = value >>> 0;
        this.buffer[this._writePos + 2] = (value >> 16);
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 0] = value;
        this._writePos += 3;
    }
    readUInt24LE() {
        if (this._limit < (this._readPos + 3)) {
            throw new BufferError("[WrappedBuffer::readUInt24] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 2] << 16) | (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 0];
        this._readPos += 3;
        return out >>> 0;
    }
    writeInt32LE(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[WrappedBuffer::writeInt32] buffer access out of bounds!");
        }
        value = value | 0;
        this.buffer[this._writePos + 3] = (value >> 24);
        this.buffer[this._writePos + 2] = (value >> 16);
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 0] = value;
        this._writePos += 4;
    }
    readInt32LE() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[WrappedBuffer::readInt32] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 3] << 24) | (this.buffer[this._readPos + 2] << 16) | (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 0];
        this._readPos += 4;
        return out;
    }
    writeUInt32LE(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[WrappedBuffer::writeInt32] buffer access out of bounds!");
        }
        value = value >>> 0;
        this.buffer[this._writePos + 3] = (value >> 24);
        this.buffer[this._writePos + 2] = (value >> 16);
        this.buffer[this._writePos + 1] = (value >> 8);
        this.buffer[this._writePos + 0] = value;
        this._writePos += 4;
    }
    readUInt32LE() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[WrappedBuffer::readInt32] buffer access out of bounds!");
        }
        const out = (this.buffer[this._readPos + 3] << 24) | (this.buffer[this._readPos + 2] << 16) | (this.buffer[this._readPos + 1] << 8) | this.buffer[this._readPos + 0];
        this._readPos += 4;
        return out >>> 0;
    }
    writeFloat32LE(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[WrappedBuffer::writeFloat32] buffer access out of bounds!");
        }
        internalFloatArray[0] = +value;
        this.buffer[this._writePos + 3] = (internalBytes[0] | 0);
        this.buffer[this._writePos + 2] = (internalBytes[1] | 0);
        this.buffer[this._writePos + 1] = (internalBytes[2] | 0);
        this.buffer[this._writePos + 0] = (internalBytes[3] | 0);
        this._writePos += 4;
    }
    readFloat32LE() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[WrappedBuffer::readFloat32] buffer access out of bounds!");
        }
        internalBytes[3] = this.buffer[this._readPos + 0];
        internalBytes[2] = this.buffer[this._readPos + 1];
        internalBytes[1] = this.buffer[this._readPos + 2];
        internalBytes[0] = this.buffer[this._readPos + 3];
        this._readPos += 4;
        return internalFloatArray[0];
    }
    writeFloat64LE(value) {
        if (this._limit < (this._writePos + 8)) {
            throw new BufferError("[WrappedBuffer::writeFloat64] buffer access out of bounds!");
        }
        internalDoubleArray[0] = +value;
        this.buffer[this._writePos + 7] = (internalBytes[0] | 0);
        this.buffer[this._writePos + 6] = (internalBytes[1] | 0);
        this.buffer[this._writePos + 5] = (internalBytes[2] | 0);
        this.buffer[this._writePos + 4] = (internalBytes[3] | 0);
        this.buffer[this._writePos + 3] = (internalBytes[4] | 0);
        this.buffer[this._writePos + 2] = (internalBytes[5] | 0);
        this.buffer[this._writePos + 1] = (internalBytes[6] | 0);
        this.buffer[this._writePos + 0] = (internalBytes[7] | 0);
        this._writePos += 8;
    }
    readFloat64LE() {
        if (this._limit < (this._readPos + 8)) {
            throw new BufferError("[WrappedBuffer::readFloat64] buffer access out of bounds!");
        }
        internalBytes[7] = this.buffer[this._readPos + 0];
        internalBytes[6] = this.buffer[this._readPos + 1];
        internalBytes[5] = this.buffer[this._readPos + 2];
        internalBytes[4] = this.buffer[this._readPos + 3];
        internalBytes[3] = this.buffer[this._readPos + 4];
        internalBytes[2] = this.buffer[this._readPos + 5];
        internalBytes[1] = this.buffer[this._readPos + 6];
        internalBytes[0] = this.buffer[this._readPos + 7];
        this._readPos += 8;
        return internalDoubleArray[0];
    }
}

class DynamicBuffer extends AbstractBuffer {
    constructor(buffers, chunkSize = 64 * 1024) {
        super();
        this.chunkSize = chunkSize;
        this.readBufferIndex = 0;
        if (chunkSize < 8) {
            throw new BufferError("Chunk size can't be smaller than 8!");
        }
        this.buffers = buffers;
        this.allocateNewBuffer();
        this.rBuffer = this.wBuffer;
    }
    static mergeBuffers(...buffers) {
        let buffs = [];
        for (const merge of buffers) {
            buffs.push(...(merge.getInternalBuffers()));
        }
        return new DynamicBuffer(buffs, 8);
    }
    getInternalBuffers() {
        return this.buffers;
    }
    static allocate(chunkSize = 64 * 1024) {
        return new DynamicBuffer([], chunkSize);
    }
    restart() {
        this.buffers = [];
        this.allocateNewBuffer();
        this.rBuffer = this.wBuffer;
        this.readBufferIndex = 0;
    }
    allocateNewBuffer() {
        this.wBuffer = new InternalBuffer(this.chunkSize);
        this.buffers.push(this.wBuffer);
    }
    nextReadBuffer() {
        this.rBuffer = this.buffers[++this.readBufferIndex];
        if (this.rBuffer === undefined) {
            throw new BufferError("Read out of bounds");
        }
    }
    writeBool(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 1)) {
            this.allocateNewBuffer();
        }
        this.wBuffer.buffer[this.wBuffer._writePos++] = (value ? 1 : -1);
    }
    readBool() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 1)) {
            this.nextReadBuffer();
        }
        return (this.rBuffer.buffer[this.rBuffer._readPos++] === 1);
    }
    writeUInt8(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 1)) {
            this.allocateNewBuffer();
        }
        this.wBuffer.buffer[this.wBuffer._writePos++] = (value | 0);
    }
    readUInt8() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 1)) {
            this.nextReadBuffer();
        }
        return this.rBuffer.buffer[this.rBuffer._readPos++];
    }
    writeInt8(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 1)) {
            this.allocateNewBuffer();
        }
        this.wBuffer.bufferS[this.wBuffer._writePos++] = (value | 0);
    }
    readInt8() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 1)) {
            this.nextReadBuffer();
        }
        return this.rBuffer.bufferS[this.rBuffer._readPos++];
    }
    writeInt16(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 2)) {
            this.allocateNewBuffer();
        }
        value = value | 0;
        this.wBuffer.bufferS[this.wBuffer._writePos + 0] = (value >> 8);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = value;
        this.wBuffer._writePos += 2;
    }
    readInt16() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 2)) {
            this.nextReadBuffer();
        }
        const out = (this.rBuffer.bufferS[this.rBuffer._readPos + 0] << 8) | this.rBuffer.buffer[this.rBuffer._readPos + 1];
        this.rBuffer._readPos += 2;
        return out;
    }
    writeUInt16(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 2)) {
            this.allocateNewBuffer();
        }
        value = value | 0;
        this.wBuffer.buffer[this.wBuffer._writePos + 0] = (value >> 8);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = value;
        this.wBuffer._writePos += 2;
    }
    readUInt16() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 2)) {
            this.nextReadBuffer();
        }
        const out = (this.rBuffer.buffer[this.rBuffer._readPos + 0] << 8) | this.rBuffer.buffer[this.rBuffer._readPos + 1];
        this.rBuffer._readPos += 2;
        return out;
    }
    writeInt24(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 3)) {
            this.allocateNewBuffer();
        }
        value = value | 0;
        this.wBuffer.bufferS[this.wBuffer._writePos + 0] = (value >> 16);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = (value >> 8);
        this.wBuffer.buffer[this.wBuffer._writePos + 2] = value;
        this.wBuffer._writePos += 3;
    }
    readInt24() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 3)) {
            this.nextReadBuffer();
        }
        const out = (this.rBuffer.bufferS[this.rBuffer._readPos + 0] << 16) |
            (this.rBuffer.buffer[this.rBuffer._readPos + 1] << 8) |
            this.rBuffer.buffer[this.rBuffer._readPos + 2];
        this.rBuffer._readPos += 3;
        return out;
    }
    writeUInt24(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 3)) {
            this.allocateNewBuffer();
        }
        value = value >>> 0;
        this.wBuffer.buffer[this.wBuffer._writePos + 0] = (value >> 16);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = (value >> 8);
        this.wBuffer.buffer[this.wBuffer._writePos + 2] = value;
        this.wBuffer._writePos += 3;
    }
    readUInt24() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 3)) {
            this.nextReadBuffer();
        }
        const out = (this.rBuffer.buffer[this.rBuffer._readPos + 0] << 16) |
            (this.rBuffer.buffer[this.rBuffer._readPos + 1] << 8) |
            this.rBuffer.buffer[this.rBuffer._readPos + 2];
        this.rBuffer._readPos += 3;
        return out >>> 0;
    }
    writeInt32(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 4)) {
            this.allocateNewBuffer();
        }
        value = value | 0;
        this.wBuffer.buffer[this.wBuffer._writePos + 0] = (value >> 24);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = (value >> 16);
        this.wBuffer.buffer[this.wBuffer._writePos + 2] = (value >> 8);
        this.wBuffer.buffer[this.wBuffer._writePos + 3] = value;
        this.wBuffer._writePos += 4;
    }
    readInt32() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 4)) {
            this.nextReadBuffer();
        }
        const out = (this.rBuffer.buffer[this.rBuffer._readPos + 0] << 24) |
            (this.rBuffer.buffer[this.rBuffer._readPos + 1] << 16) |
            (this.rBuffer.buffer[this.rBuffer._readPos + 2] << 8) |
            this.rBuffer.buffer[this.rBuffer._readPos + 3];
        this.rBuffer._readPos += 4;
        return out;
    }
    writeUInt32(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 4)) {
            this.allocateNewBuffer();
        }
        value = value >>> 0;
        this.wBuffer.buffer[this.wBuffer._writePos + 0] = (value >> 24);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = (value >> 16);
        this.wBuffer.buffer[this.wBuffer._writePos + 2] = (value >> 8);
        this.wBuffer.buffer[this.wBuffer._writePos + 3] = value;
        this.wBuffer._writePos += 4;
    }
    readUInt32() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 4)) {
            this.nextReadBuffer();
        }
        const out = (this.rBuffer.buffer[this.rBuffer._readPos + 0] << 24) |
            (this.rBuffer.buffer[this.rBuffer._readPos + 1] << 16) |
            (this.rBuffer.buffer[this.rBuffer._readPos + 2] << 8) |
            this.rBuffer.buffer[this.rBuffer._readPos + 3];
        this.rBuffer._readPos += 4;
        return out >>> 0;
    }
    writeFloat32(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 4)) {
            this.allocateNewBuffer();
        }
        internalFloatArray[0] = +value;
        this.wBuffer.buffer[this.wBuffer._writePos + 0] = (internalBytes[0] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = (internalBytes[1] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 2] = (internalBytes[2] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 3] = (internalBytes[3] | 0);
        this.wBuffer._writePos += 4;
    }
    readFloat32() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 4)) {
            this.nextReadBuffer();
        }
        internalBytes[0] = this.rBuffer.buffer[this.rBuffer._readPos + 0];
        internalBytes[1] = this.rBuffer.buffer[this.rBuffer._readPos + 1];
        internalBytes[2] = this.rBuffer.buffer[this.rBuffer._readPos + 2];
        internalBytes[3] = this.rBuffer.buffer[this.rBuffer._readPos + 3];
        this.rBuffer._readPos += 4;
        return internalFloatArray[0];
    }
    writeFloat64(value) {
        if (this.wBuffer._limit < (this.wBuffer._writePos + 8)) {
            this.allocateNewBuffer();
        }
        internalDoubleArray[0] = +value;
        this.wBuffer.buffer[this.wBuffer._writePos + 0] = (internalBytes[0] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 1] = (internalBytes[1] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 2] = (internalBytes[2] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 3] = (internalBytes[3] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 4] = (internalBytes[4] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 5] = (internalBytes[5] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 6] = (internalBytes[6] | 0);
        this.wBuffer.buffer[this.wBuffer._writePos + 7] = (internalBytes[7] | 0);
        this.wBuffer._writePos += 8;
    }
    readFloat64() {
        if (this.rBuffer._writePos < (this.rBuffer._readPos + 8)) {
            this.nextReadBuffer();
        }
        internalBytes[0] = this.rBuffer.buffer[this.rBuffer._readPos + 0];
        internalBytes[1] = this.rBuffer.buffer[this.rBuffer._readPos + 1];
        internalBytes[2] = this.rBuffer.buffer[this.rBuffer._readPos + 2];
        internalBytes[3] = this.rBuffer.buffer[this.rBuffer._readPos + 3];
        internalBytes[4] = this.rBuffer.buffer[this.rBuffer._readPos + 4];
        internalBytes[5] = this.rBuffer.buffer[this.rBuffer._readPos + 5];
        internalBytes[6] = this.rBuffer.buffer[this.rBuffer._readPos + 6];
        internalBytes[7] = this.rBuffer.buffer[this.rBuffer._readPos + 7];
        this.rBuffer._readPos += 8;
        return internalDoubleArray[0];
    }
    writeBuffer(buffer) {
        /*if (this.wBuffer._limit < (this.wBuffer._writePos + 8)) {
            this.allocateNewBuffer();
        }*/
        let offset = 0;
        while (offset !== buffer.byteLength) {
            if (this.wBuffer._limit === this.wBuffer._writePos) {
                this.allocateNewBuffer();
            }
            let available = this.wBuffer._limit - this.wBuffer._writePos;
            available = Math.min(available, buffer.byteLength - offset);
            this.wBuffer.buffer.set(buffer.subarray(offset, offset + available), this.wBuffer._writePos);
            this.wBuffer._writePos += available;
            offset += available;
        }
    }
    readBuffer(size) {
        let buffer = new Uint8Array(size);
        let offset = 0;
        while (offset !== buffer.byteLength) {
            if (this.rBuffer._writePos === this.rBuffer._readPos) {
                this.nextReadBuffer();
            }
            let available = this.rBuffer._writePos - this.rBuffer._readPos;
            available = Math.min(available, buffer.byteLength - offset);
            buffer.set(this.rBuffer.buffer.subarray(this.rBuffer._readPos, this.rBuffer._readPos + available), offset);
            this.rBuffer._readPos += available;
            offset += available;
        }
        return buffer;
    }
    toBuffer() {
        let length = 0;
        for (const buffer of this.buffers) {
            length += buffer._writePos;
        }
        let out = new Uint8Array(length);
        let offset = 0;
        for (const buffer of this.buffers) {
            out.set(new Uint8Array(buffer._buffer, 0, buffer._writePos), offset);
            offset += buffer._writePos;
        }
        return out;
    }
}

class SimpleBuffer extends Uint8Array {
    constructor(buffer, offset, length) {
        super(buffer, offset, length);
        this.offset = offset;
        this.bufferS = new Int8Array(buffer, offset, length);
        this._readPos = 0;
        this._writePos = 0;
        this._limit = this.byteLength;
    }
    static fromBuffer(buffer) {
        if (!(buffer instanceof Uint8Array)) {
            throw new BufferError("Couldn't create SimpleBuffer(fromBuffer), invalid constructor argument " + typeof (buffer) + ", expected Uint8Array!");
        }
        return new SimpleBuffer(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    }
    static fromArrayBuffer(buffer) {
        if (!(buffer instanceof ArrayBuffer)) {
            throw new BufferError("Couldn't create SimpleBuffer(fromArrayBuffer), invalid constructor argument " + typeof (buffer) + ", expected ArrayBuffer!");
        }
        return new SimpleBuffer(buffer, 0, buffer.byteLength);
    }
    static allocate(size) {
        size = size | 0;
        return new SimpleBuffer(new ArrayBuffer(size), 0, size);
    }
    subBuffer(start, length) {
        return new SimpleBuffer(this, this.offset + start, length);
    }
    set writePos(value) {
        if (value > this._limit) {
            throw new BufferError("[SimpleBuffer::writePos] Can't set writePos above buffer's capacity!");
        }
        if (value < 0) {
            throw new BufferError("[SimpleBuffer::writePos] Can't set writePos below 0!");
        }
        this._writePos = value;
    }
    get writePos() {
        return this._writePos;
    }
    set readPos(value) {
        if (value > this._limit) {
            throw new BufferError("[SimpleBuffer::readPos] Can't set readPos above buffer's capacity!");
        }
        if (value < 0) {
            throw new BufferError("[SimpleBuffer::readPos] Can't set readPos below 0!");
        }
        this._readPos = value;
    }
    get readPos() {
        return this._readPos;
    }
    set limit(value) {
        if (value > this.buffer.byteLength) {
            throw new BufferError("[SimpleBuffer::limit] Can't set limit above buffer's capacity!");
        }
        if (value < 0) {
            throw new BufferError("[SimpleBuffer::limit] Can't set limit below 0!");
        }
        this._limit = value;
    }
    get limit() {
        return this._limit;
    }
    resetLimit() {
        this.limit = this.buffer.byteLength;
    }
    restart() {
        this._readPos = 0;
        this._writePos = 0;
    }
    writeBool(value) {
        if (this._limit < (this._writePos + 1)) {
            throw new BufferError("[SimpleBuffer::writeBool] buffer access out of bounds!");
        }
        this[this._writePos++] = (value ? 1 : -1);
    }
    readBool() {
        if (this._limit < (this._readPos + 1)) {
            throw new BufferError("[SimpleBuffer::readBool] buffer access out of bounds!");
        }
        return (this[this._readPos++] === 1);
    }
    writeUInt8(value) {
        if (this._limit < (this._writePos + 1)) {
            throw new BufferError("[SimpleBuffer::writeUByte] buffer access out of bounds!");
        }
        this[this._writePos++] = (value | 0);
    }
    readUInt8() {
        if (this._limit < (this._readPos + 1)) {
            throw new BufferError("[SimpleBuffer::readUByte] buffer access out of bounds!");
        }
        return this[this._readPos++];
    }
    writeInt8(value) {
        if (this._limit < (this._writePos + 1)) {
            throw new BufferError("[SimpleBuffer::writeByte] buffer access out of bounds!");
        }
        this.bufferS[this._writePos++] = (value | 0);
    }
    readInt8() {
        if (this._limit < (this._readPos + 1)) {
            throw new BufferError("[SimpleBuffer::readByte] buffer access out of bounds!");
        }
        return this.bufferS[this._readPos++];
    }
    writeInt16(value) {
        if (this._limit < (this._writePos + 2)) {
            throw new BufferError("[SimpleBuffer::writeInt16] buffer access out of bounds!");
        }
        value = value | 0;
        this.bufferS[this._writePos + 0] = (value >> 8);
        this[this._writePos + 1] = value;
        this._writePos += 2;
    }
    readInt16() {
        if (this._limit < (this._readPos + 2)) {
            throw new BufferError("[SimpleBuffer::readInt16] buffer access out of bounds!");
        }
        const out = (this.bufferS[this._readPos + 0] << 8) | this[this._readPos + 1];
        this._readPos += 2;
        return out;
    }
    writeUInt16(value) {
        if (this._limit < (this._writePos + 2)) {
            throw new BufferError("[SimpleBuffer::writeUInt16] buffer access out of bounds!");
        }
        value = value | 0;
        this[this._writePos + 0] = (value >> 8);
        this[this._writePos + 1] = value;
        this._writePos += 2;
    }
    readUInt16() {
        if (this._limit < (this._readPos + 2)) {
            throw new BufferError("[SimpleBuffer::readUInt16] buffer access out of bounds!");
        }
        const out = (this[this._readPos + 0] << 8) | this[this._readPos + 1];
        this._readPos += 2;
        return out;
    }
    writeInt24(value) {
        if (this._limit < (this._writePos + 3)) {
            throw new BufferError("[SimpleBuffer::writeInt24] buffer access out of bounds!");
        }
        value = value | 0;
        this.bufferS[this._writePos + 0] = (value >> 16);
        this[this._writePos + 1] = (value >> 8);
        this[this._writePos + 2] = value;
        this._writePos += 3;
    }
    readInt24() {
        if (this._limit < (this._readPos + 3)) {
            throw new BufferError("[SimpleBuffer::readInt24] buffer access out of bounds!");
        }
        const out = (this.bufferS[this._readPos + 0] << 16) | (this[this._readPos + 1] << 8) | this[this._readPos + 2];
        this._readPos += 3;
        return out;
    }
    writeUInt24(value) {
        if (this._limit < (this._writePos + 3)) {
            throw new BufferError("[SimpleBuffer::writeUInt24] buffer access out of bounds!");
        }
        value = value >>> 0;
        this[this._writePos + 0] = (value >> 16);
        this[this._writePos + 1] = (value >> 8);
        this[this._writePos + 2] = value;
        this._writePos += 3;
    }
    readUInt24() {
        if (this._limit < (this._readPos + 3)) {
            throw new BufferError("[SimpleBuffer::readUInt24] buffer access out of bounds!");
        }
        const out = (this[this._readPos + 0] << 16) | (this[this._readPos + 1] << 8) | this[this._readPos + 2];
        this._readPos += 3;
        return out >>> 0;
    }
    writeInt32(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[SimpleBuffer::writeInt32] buffer access out of bounds!");
        }
        value = value | 0;
        this[this._writePos + 0] = (value >> 24);
        this[this._writePos + 1] = (value >> 16);
        this[this._writePos + 2] = (value >> 8);
        this[this._writePos + 3] = value;
        this._writePos += 4;
    }
    readInt32() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[SimpleBuffer::readInt32] buffer access out of bounds!");
        }
        const out = (this[this._readPos + 0] << 24) | (this[this._readPos + 1] << 16) | (this[this._readPos + 2] << 8) | this[this._readPos + 3];
        this._readPos += 4;
        return out;
    }
    writeUInt32(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[SimpleBuffer::writeInt32] buffer access out of bounds!");
        }
        value = value >>> 0;
        this[this._writePos + 0] = (value >> 24);
        this[this._writePos + 1] = (value >> 16);
        this[this._writePos + 2] = (value >> 8);
        this[this._writePos + 3] = value;
        this._writePos += 4;
    }
    readUInt32() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[SimpleBuffer::readInt32] buffer access out of bounds!");
        }
        const out = (this[this._readPos + 0] << 24) | (this[this._readPos + 1] << 16) | (this[this._readPos + 2] << 8) | this[this._readPos + 3];
        this._readPos += 4;
        return out >>> 0;
    }
    writeFloat32(value) {
        if (this._limit < (this._writePos + 4)) {
            throw new BufferError("[SimpleBuffer::writeFloat32] buffer access out of bounds!");
        }
        internalFloatArray[0] = +value;
        this[this._writePos + 0] = (internalBytes[0] | 0);
        this[this._writePos + 1] = (internalBytes[1] | 0);
        this[this._writePos + 2] = (internalBytes[2] | 0);
        this[this._writePos + 3] = (internalBytes[3] | 0);
        this._writePos += 4;
    }
    readFloat32() {
        if (this._limit < (this._readPos + 4)) {
            throw new BufferError("[SimpleBuffer::readFloat32] buffer access out of bounds!");
        }
        internalBytes[0] = this[this._readPos + 0];
        internalBytes[1] = this[this._readPos + 1];
        internalBytes[2] = this[this._readPos + 2];
        internalBytes[3] = this[this._readPos + 3];
        this._readPos += 4;
        return internalFloatArray[0];
    }
    writeFloat64(value) {
        if (this._limit < (this._writePos + 8)) {
            throw new BufferError("[SimpleBuffer::writeFloat64] buffer access out of bounds!");
        }
        internalDoubleArray[0] = +value;
        this[this._writePos + 0] = (internalBytes[0] | 0);
        this[this._writePos + 1] = (internalBytes[1] | 0);
        this[this._writePos + 2] = (internalBytes[2] | 0);
        this[this._writePos + 3] = (internalBytes[3] | 0);
        this[this._writePos + 4] = (internalBytes[4] | 0);
        this[this._writePos + 5] = (internalBytes[5] | 0);
        this[this._writePos + 6] = (internalBytes[6] | 0);
        this[this._writePos + 7] = (internalBytes[7] | 0);
        this._writePos += 8;
    }
    readFloat64() {
        if (this._limit < (this._readPos + 8)) {
            throw new BufferError("[SimpleBuffer::readFloat64] buffer access out of bounds!");
        }
        internalBytes[0] = this[this._readPos + 0];
        internalBytes[1] = this[this._readPos + 1];
        internalBytes[2] = this[this._readPos + 2];
        internalBytes[3] = this[this._readPos + 3];
        internalBytes[4] = this[this._readPos + 4];
        internalBytes[5] = this[this._readPos + 5];
        internalBytes[6] = this[this._readPos + 6];
        internalBytes[7] = this[this._readPos + 7];
        this._readPos += 8;
        return internalDoubleArray[0];
    }
    setInt32(offset, value) {
        if (this._limit < (offset + 4)) {
            throw new BufferError("[SimpleBuffer::setInt32] buffer access out of bounds!");
        }
        offset = offset | 0;
        value = value | 0;
        this[offset] = (value >> 24);
        this[offset + 1] = (value >> 16);
        this[offset + 2] = (value >> 8);
        this[offset + 3] = value;
    }
    writeBuffer(buffer) {
        this.set(buffer, this._writePos);
        this._writePos += buffer.byteLength;
    }
    readBuffer(size) {
        let buffer = this.buffer.slice(this._readPos, size);
        this._readPos += buffer.byteLength;
        return new Uint8Array(buffer);
    }
    toBuffer() {
        return this.buffer.slice(0, this.writePos);
    }
    toUint8Array() {
        return new Uint8Array(this.toBuffer());
    }
    //******************************************************************************/
    // From AbstractBuffer
    writeUByte(value) {
        this.writeUInt8(value);
    }
    readUByte() {
        return this.readUInt8();
    }
    writeByte(value) {
        this.writeInt8(value);
    }
    readByte() {
        return this.readInt8();
    }
    readString() {
        const length = this.readUInt32();
        let buf = this.readBuffer(length);
        return this.stringFromBuffer(buf);
    }
    writeString(string) {
        let buf = this.bufferFromString(string);
        this.writeUInt32(buf.byteLength);
        this.writeBuffer(buf);
    }
    readVarInt() {
        let value = 0;
        let digits = 0;
        while (value <= 0) {
            let next = this.readInt8();
            if (next < 0) {
                value = (next << digits) + value;
            }
            else {
                value = (next << digits) - value;
            }
            digits += 7;
        }
        return value;
    }
    writeVarInt(value) {
        while (value >= 128) {
            this.writeInt8(-(value & 0x7F));
            value >>>= 7;
        }
        this.writeInt8((value & 0x7F));
    }
    readStringMode(mode) {
        if (mode === StringMode.Fixed8) {
            const length = this.readUInt8();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Fixed16) {
            const length = this.readUInt16();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Fixed24) {
            const length = this.readUInt24();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Fixed32) {
            const length = this.readUInt32();
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else if (mode === StringMode.Dynamic) {
            let length = 0;
            let digits = 0;
            while (length <= 0) {
                let next = this.readInt8();
                if (next < 0) {
                    length = (next << digits) + length;
                }
                else {
                    length = (next << digits) - length;
                }
                digits += 7;
            }
            let buf = this.readBuffer(length);
            return this.stringFromBuffer(buf);
        }
        else {
            throw new Error("Invalid StringMode!");
        }
    }
    writeStringMode(mode, string) {
        if (mode === StringMode.Fixed8) {
            let buf = this.bufferFromString(string);
            this.writeUInt8(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Fixed16) {
            let buf = this.bufferFromString(string);
            this.writeUInt16(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Fixed24) {
            let buf = this.bufferFromString(string);
            this.writeUInt24(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Fixed32) {
            let buf = this.bufferFromString(string);
            this.writeUInt32(buf.byteLength);
            this.writeBuffer(buf);
        }
        else if (mode === StringMode.Dynamic) {
            let buf = this.bufferFromString(string);
            let length = buf.byteLength;
            while (length >= 128) {
                this.writeInt8(-(length & 0x7F));
                length >>>= 7;
            }
            this.writeInt8(length);
            this.writeBuffer(buf);
        }
        else {
            throw new Error("Invalid StringMode!");
        }
    }
    bufferFromString(string) {
        // browser optimization
        if (global.TextEncoder !== undefined) {
            let encoder = new TextEncoder();
            return encoder.encode(string);
        }
        return BinaryUtils.stringToUint8Array(string);
    }
    stringFromBuffer(buffer) {
        // browser optimization
        if (global.TextDecoder !== undefined) {
            let decoder = new TextDecoder("utf-8");
            return decoder.decode(buffer);
        }
        return BinaryUtils.uint8ToStringArray(buffer);
    }
}

let base85Encoder = new Base85("0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ.-:+=^!/*?&_;()[]{}@%$#");
class InternalEncryptionUtils {
    static sha256(text) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield crypto.subtle.digest("SHA-256", BinaryUtils.stringToUint8Array(text));
            }
            catch (e) {
                throw new Error("Failed to sha256!");
            }
        });
    }
    static aesGcmEncrypt(data, key) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof (data) === "string") {
                data = Buffer.from(data);
            }
            try {
                const keyBuffer = typeof (key) === "string" ? yield this.sha256(key) : key;
                const iv = crypto.getRandomValues(new Uint8Array(12));
                if (!iv) {
                    throw new Error("Failed to created IV!");
                }
                const alg = { name: "AES-GCM", iv: iv };
                const cryptoKey = yield crypto.subtle.importKey("raw", keyBuffer, alg.name, false, ["encrypt"]);
                const encryptedBuffer = yield crypto.subtle.encrypt(alg, cryptoKey, data);
                return Buffer.concat([Buffer.from(iv.buffer), Buffer.from(encryptedBuffer)]);
            }
            catch (e) {
                throw new Error("Failed to encrypt!");
            }
        });
    }
    static aesGcmDecrypt(encrypted, key) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const keyBuffer = typeof (key) === "string" ? yield this.sha256(key) : key;
                const iv = encrypted.slice(0, 12);
                const data = encrypted.slice(12, -16);
                const alg = { name: "AES-GCM", iv: new Uint8Array(iv) };
                const cryptoKey = yield crypto.subtle.importKey("raw", keyBuffer, alg.name, false, ["decrypt"]);
                const decryptedBuffer = new Uint8Array(yield crypto.subtle.decrypt(alg, cryptoKey, data));
                const text = BinaryUtils.uint8ToStringArray(decryptedBuffer);
                return text;
            }
            catch (e) {
                throw new Error("Failed to decrypt!");
            }
        });
    }
    static aesGcmDecryptBuffer(encrypted, key) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const keyBuffer = typeof (key) === "string" ? yield this.sha256(key) : key;
                const iv = encrypted.slice(0, 12);
                const data = encrypted.slice(12, -16);
                const alg = { name: "AES-GCM", iv: new Uint8Array(iv) };
                const cryptoKey = yield crypto.subtle.importKey("raw", keyBuffer, alg.name, false, ["decrypt"]);
                return Buffer.from(yield crypto.subtle.decrypt(alg, cryptoKey, data));
            }
            catch (e) {
                throw new Error("Failed to decrypt!");
            }
        });
    }
    static aesGcmEncryptBase85(data, key) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof (data) === "string") {
                data = Buffer.from(data);
            }
            try {
                const keyBuffer = typeof (key) === "string" ? yield this.sha256(key) : key;
                const iv = crypto.getRandomValues(new Uint8Array(12));
                if (!iv) {
                    throw new Error("Failed to created IV!");
                }
                const alg = { name: "AES-GCM", iv: iv };
                const cryptoKey = yield crypto.subtle.importKey("raw", keyBuffer, alg.name, false, ["encrypt"]);
                const encryptedBuffer = yield crypto.subtle.encrypt(alg, cryptoKey, data);
                return base85Encoder.encode(iv) + "|" + base85Encoder.encode(new Uint8Array(encryptedBuffer));
            }
            catch (e) {
                throw new Error("Failed to encrypt!");
            }
        });
    }
    static aesGcmDecryptBase85(encrypted, key) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const keyBuffer = typeof (key) === "string" ? yield this.sha256(key) : key;
                const [iv, data] = encrypted.split("|").map(v => base85Encoder.decode(v));
                const alg = { name: "AES-GCM", iv: new Uint8Array(iv) };
                const cryptoKey = yield crypto.subtle.importKey("raw", keyBuffer, alg.name, false, ["decrypt"]);
                const decryptedBuffer = new Uint8Array(yield crypto.subtle.decrypt(alg, cryptoKey, data));
                const text = BinaryUtils.uint8ToStringArray(decryptedBuffer);
                return text;
            }
            catch (e) {
                throw new Error("Failed to decrypt!");
            }
        });
    }
    static aesGcmDecryptBufferBase85(encrypted, key) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const keyBuffer = typeof (key) === "string" ? yield this.sha256(key) : key;
                const [iv, data] = encrypted.split("|").map(v => base85Encoder.decode(v));
                const alg = { name: "AES-GCM", iv: new Uint8Array(iv) };
                const cryptoKey = yield crypto.subtle.importKey("raw", keyBuffer, alg.name, false, ["decrypt"]);
                return Buffer.from(yield crypto.subtle.decrypt(alg, cryptoKey, data));
            }
            catch (e) {
                throw new Error("Failed to decrypt!");
            }
        });
    }
}

class QueueItem {
    constructor(callback) {
        this.callback = callback;
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
    resolve(val) { }
    reject(err) { }
    execute(queue) {
        return this.callback().then((val) => {
            this.resolve(val);
            queue.onItemFinished(this);
        }, (err) => {
            this.reject(err);
            queue.onItemError(this, err);
        });
    }
}
class AsyncQueue {
    constructor(config) {
        this.queue = [];
        this.slots = 1;
        this.running = 0;
        this.scheduled = false;
        this.manualStart = false;
        this.finishing = false;
        this.finished = false;
        this.stopOnError = false;
        this.waitOnError = false;
        this.crashed = false;
        this.lastItemCallback = () => { };
        this._lastItemCallback = () => { };
        this.errorItemCallback = () => { };
        this._lastItemError = () => { };
        if (config !== undefined) {
            if (config.slots !== undefined) {
                this.slots = config.slots | 0;
            }
            if (config.stopOnError === true) {
                this.stopOnError = true;
            }
            if (config.waitOnError === true) {
                this.waitOnError = true;
            }
        }
        this.active = !this.manualStart;
    }
    add(callback) {
        if (this.finished) {
            throw new Error("Queue already finished!");
        }
        let item = new QueueItem(callback);
        this.queue.push(item);
        this.trySchedule();
        return item.promise;
    }
    onItemFinished(item) {
        this.slots++;
        this.running--;
        if (this.crashed === true) {
            if (this.waitOnError) {
                if (this.running === 0) {
                    if (this.finishing) {
                        this._lastItemCallback();
                        this.finished = true;
                    }
                }
            }
        }
        else if (this.queue.length > 0) {
            this.trySchedule();
        }
        else if (this.running === 0) {
            this.lastItemCallback();
            if (this.finishing) {
                this._lastItemCallback();
                this.finished = true;
            }
        }
    }
    onItemError(item, err) {
        this.slots++;
        this.running--;
        if (this.stopOnError) {
            this.queue = []; // nuke the queue if we are stopping on error
            this.active = false;
            if (this.waitOnError) {
                if (this.crashed === false) {
                    this._lastItemCallback = () => {
                        this.errorItemCallback(err);
                        this._lastItemError(err);
                    };
                    this.crashed = true;
                }
                if (this.running === 0) {
                    this._lastItemCallback();
                }
            }
            else {
                this.errorItemCallback(err);
                this.running = 0;
                this.crashed = true;
                if (this.finishing && !this.waitOnError) {
                    this._lastItemError(err);
                    this.finished = true;
                }
            }
        }
        else {
            if (this.crashed === true) {
                return;
            }
            this.errorItemCallback(err);
            if (this.queue.length > 0) {
                this.trySchedule();
            }
            else if (this.running === 0) {
                this.lastItemCallback();
                if (this.finishing && this.waitOnError) {
                    this._lastItemError(err);
                }
            }
            if (this.finishing && !this.waitOnError) {
                this._lastItemError(err);
                this.finished = true;
            }
        }
    }
    trySchedule() {
        if (this.active === true && this.scheduled === false && this.queue.length > 0 && this.slots > 0) {
            this.scheduled = true;
            process.nextTick(() => {
                this.run();
                this.scheduled = false;
            });
        }
    }
    run() {
        while (this.queue.length > 0 && this.slots > 0) {
            let newItem = this.queue.shift();
            if (newItem !== undefined) {
                this.slots--;
                this.running++;
                newItem.execute(this);
            }
        }
    }
    finish() {
        this.finishing = true;
        if (this.running === 0 && this.queue.length === 0) {
            this.finished = true;
            return Promise.resolve();
        }
        else {
            return new Promise((resolve, reject) => {
                this._lastItemCallback = resolve;
                this._lastItemError = reject;
            });
        }
    }
}

export { AbstractBuffer, InternalBuffer, StringMode, InternalEncryptionUtils as EncryptionUtils, Services, Service, ServiceClass, ClassVariable, ClassMeta, GetClassMeta, RegisterClass, RegisterClassVariable, UniqueArray, MapWithDefault, DataProviderVariable, VARIABLE, Class, DataProvider, Base64, Base85, Benchmark, BinaryUtils, Color, ColorUtils, ErrorResponse, InternalError, BasicEvent, PromiseEvent, HaxCompressor, HaxEncryptor3, HaxRNG, Noise, StringUtils, TextUtils, Time, Utils, ObjectUtils, BaseClass, Mat4, Quat, Vec2, Vec3, WrappedBuffer, DynamicBuffer, SimpleBuffer, BufferError, AsyncQueue };

//# sourceMappingURL=index.js.map